// What each brand collects, read from the live tables and forms (Oct 2026): Resident Service 360's database, and for BedMaps the
// bedmaps.com forms and Find Housing list plus the BedMaps app's own database (Supabase project "bedmaps").
// Brand > category (with a one-line `summary` for the list of categories, and for BedMaps the `section` it belongs to) >
// record type > group > [what we record, an example].

const slug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
/** A record type: its name, and its groups as [group, [[what we record, example], ...]]. */
const record = (name, groups) => ({ key: slug(name), name, groups });

export const catalogue = [
  {
    key: 'rs360',
    brand: 'Resident Service 360',
    categories: [
      {
        key: 'users',
        name: 'Users',
        summary: 'For everyone: name, email, mobile number, how they sign in, terms and consents, notification choices and devices. Then what we keep for each type: residents, business owners, trades, commercial buyers, housing providers and staff',
        records: [
          record('Every user', [
            ['Account', [
              ['Full name', 'Amara Okafor'],
              ['Email address', 'amara.okafor@gmail.com'],
              ['Whether the email address is confirmed', 'Confirmed on 12 Oct 2026'],
              ['Mobile number, when they sign up in the app', '07700 900123'],
              ['The kind of account they chose at sign-up', 'Resident'],
              ['When they joined', '12 Oct 2026'],
            ]],
            ['Signing in', [
              ['How they sign in: email and password, Google or Apple', 'Apple'],
              ['Profile picture, when they sign in with Google', 'Their Google photo'],
              ['When they last signed in', '28 Oct 2026, 09:14'],
            ]],
            ['Terms and consents', [
              ['Which terms they accepted, and when', 'Version of 1 Oct 2026, accepted 12 Oct 2026'],
              ['Whether they agreed to the photo checker, and when', 'Agreed on 12 Oct 2026'],
            ]],
            ['Notifications', [
              ['For each kind of alert, whether they want it by push, email or in the app', 'Repair updates: push and email'],
              ['The phones and devices we send push alerts to', 'iPhone'],
              ['When each device was last seen', '28 Oct 2026'],
              ['The alerts we sent them', '“Your repair is booked for Thursday”'],
              ['Whether each alert was read or archived', 'Read on 28 Oct 2026'],
            ]],
            ['Messages', [
              ['Messages they send and receive', '“Can you come before 10am?”'],
              ['Contact details they choose to share', '07700 900123'],
            ]],
            ['Safety', [
              ['Reports they make, or that are about them', 'Reported a message as abusive'],
              ['People and companies they block', 'Blocked one trade'],
              ['Suspensions and appeals', 'Suspended for 7 days, appeal waiting'],
            ]],
            ['Closing an account', [
              ['Requests to delete their account', 'Asked on 20 Oct 2026'],
              ['How far each request has got', 'Finished'],
            ]],
          ]),
          record('Residents', [
            ['Referral profile', [
              ['Legal name', 'Amara Ngozi Okafor'],
              ['Preferred name', 'Amara'],
              ['Date of birth', '3 Mar 1994'],
              ['Phone number', '07700 900123'],
              ['Email address', 'amara.okafor@gmail.com'],
              ['Current address and postcode', 'Flat 2, 14 Park Road, Leeds LS6 2AB'],
            ]],
            ['Referral documents', [
              ['Kind of document', 'Proof of income'],
              ['Title', 'Universal Credit statement, Sept 2026'],
              ['The file', 'uc-statement-sept.pdf'],
              ['Issue and expiry dates', 'Issued 1 Sep 2026, no expiry'],
            ]],
            ['Sharing referral documents', [
              ['Who they share it with', 'Northfield Housing Trust'],
              ['Which parts and documents', 'Profile, ID and benefit letter'],
              ['Why', 'Room application'],
              ['When the share ends', '30 Nov 2026'],
              ['Requests to see it', 'Parkside Lettings asked on 15 Oct 2026'],
              ['Each time it is viewed', 'Viewed by Northfield on 16 Oct 2026'],
            ]],
            ['ID checks', [
              ['Photos of their ID', 'Front and back of a driving licence'],
              ['Type of ID', 'Driving licence'],
              ['Name and date of birth on the ID', 'Amara N Okafor, 3 Mar 1994'],
              ['The result', 'Approved'],
            ]],
            ['Housing profile', [
              ['Benefits', 'Universal Credit, PIP'],
              ['Gender', 'Woman'],
              ['Support needs', 'Managing bills and benefit forms'],
              ['Councils they have a connection to', 'Leeds, Bradford'],
              ['Access needs', 'Ground-floor room'],
              ['Consent to use health details', 'Agreed on 12 Oct 2026'],
            ]],
            ['Past homes', [
              ['Address and postcode', '22 Elm Street, Bradford BD3 9QT'],
              ['When they moved in and out', 'Mar 2021 to Aug 2024'],
              ['Tenancy type', 'Assured shorthold tenancy'],
              ['Landlord', 'J Patel'],
            ]],
            ['Current home', [
              ['Country', 'England'],
              ['Tenancy type', 'Licence'],
              ['Kind of accommodation', 'Supported housing'],
              ['Kind of agreement', 'Licence agreement'],
            ]],
            ['Their homes', [
              ['Address', 'Flat 2, 14 Park Road, Leeds'],
              ['Postcode', 'LS6 2AB'],
              ['The housing provider they are linked to', 'Northfield Housing Trust'],
              ['Requests to link a home to a provider', 'Asked Northfield on 13 Oct 2026, approved'],
            ]],
            ['Landlord or agent', [
              ['Landlord name', 'J Patel'],
              ['Agency and contact name', 'Parkside Lettings, Tom Reid'],
              ['Email and phone', 'tom@parksidelettings.co.uk, 0113 496 0000'],
              ['Whether we may contact them', 'Yes'],
            ]],
            ['Repairs', [
              ['What is wrong', 'Kitchen tap leaking under the sink'],
              ['How urgent it is', 'Urgent'],
              ['Status and dates', 'Booked for 30 Oct 2026'],
              ['Photos', 'Two photos of the sink'],
              ['Updates', '“Plumber booked for Thursday”'],
              ['What the photo checker found', 'Leak at the tap joint, likely a worn washer'],
              ['Feedback', '“Fixed quickly, thank you”'],
            ]],
            ['Rooms', [
              ['Applications: their message, what they shared, and the decision', '“I work nights, so a quiet room suits me”, shared profile and ID, viewing offered'],
              ['Saved rooms', 'Three rooms in Leeds'],
            ]],
            ['Home jobs', [
              ['Title and description', 'Replace bathroom extractor fan'],
              ['Area and exact address', 'LS6, Flat 2, 14 Park Road'],
              ['Deadline', '15 Nov 2026'],
              ['Bids they receive', 'Three bids, from £90 to £140'],
              ['The work, visits, invoices, payments and reviews that follow', 'Paid £120 by card, left 5 stars'],
            ]],
          ]),
          record('Business owners', [
            ['Premises', [
              ['Business name', 'The Corner Café'],
              ['Address', '41 High Street, York'],
              ['Postcode', 'YO1 8RN'],
            ]],
            ['Repairs and jobs', [
              ['What is wrong', 'Walk-in fridge not cooling'],
              ['How urgent it is', 'Emergency'],
              ['Photos', 'A photo of the fridge display'],
              ['Status', 'Taking bids'],
            ]],
            ['After a job', [
              ['Bids they receive', 'Two bids'],
              ['The work and visits', 'Visit on 2 Nov 2026 at 8am'],
              ['Invoices and payments', '£260, paid by card'],
              ['Reviews they leave', '4 stars, “On time and tidy”'],
            ]],
          ]),
          record('Trades', [
            ['Company', [
              ['Name', 'Brightline Plumbing Ltd'],
              ['Registration number', '12345678'],
              ['Regulator number', 'Gas Safe 654321'],
              ['Description', '“Plumbing and heating across West Yorkshire”'],
              ['Trades', 'Plumbing, heating'],
              ['Areas served', 'Leeds, Bradford, Wakefield'],
            ]],
            ['Checks', [
              ['The Companies House check', 'Active, name matches'],
              ['Accreditations and their reference numbers', 'Gas Safe 654321'],
            ]],
            ['Team', [
              ['Members and their roles', 'Dan Hughes, owner; Priya Shah, engineer'],
              ['Invitations', 'priya@brightlineplumbing.co.uk, accepted'],
              ['A log of team changes', 'Dan made Priya an admin on 20 Oct 2026'],
            ]],
            ['Location', [
              ['Postcode area of their base', 'LS11'],
              ['Rough location, for distance search', 'Near Leeds city centre'],
            ]],
            ['Bids', [
              ['Message', '“Can attend Thursday morning”'],
              ['Price', '£120'],
              ['Status', 'Accepted'],
            ]],
            ['Work', [
              ['Status', 'Finished'],
              ['Visits', '30 Oct 2026, 9am to 11am'],
              ['Photos', 'After photo of the new tap'],
              ['When it was finished', '30 Oct 2026'],
            ]],
            ['Invoices and payouts', [
              ['Legal name, address and company number', 'Brightline Plumbing Ltd, 5 Mill Lane, Leeds LS11 5AA, 12345678'],
              ['Amount', '£120'],
              ['Payout account and its country', 'Stripe account, United Kingdom'],
            ]],
            ['Subscription', [
              ['Apple or card subscription', 'Card, monthly'],
              ['Valid until', '30 Nov 2026'],
              ['Bids counted against their allowance', '4 of 5 free bids used this month'],
            ]],
            ['Reviews', [
              ['Reviews of their work', '5 stars, “Fixed quickly”'],
              ['Their replies', '“Thanks Amara, glad it is sorted”'],
            ]],
            ['Finding work', [
              ['Saved jobs', 'Six jobs'],
              ['Saved searches', 'Plumbing within 10 miles of LS11'],
              ['Job alerts', 'Three alerts this week'],
            ]],
            ['Government tenders', [
              ['Their responses', 'Council repairs contract, in progress'],
              ['Their checklists', '4 of 9 steps done'],
            ]],
          ]),
          record('Commercial buyers', [
            ['Company', [
              ['Name', 'Castlegate Facilities Ltd'],
              ['Registration number', '09876543'],
              ['Description', '“Facilities management for offices in York”'],
            ]],
            ['Team', [
              ['Members and their roles', 'Ruth Bell, facilities manager'],
              ['Invitations', 'finance@castlegatefm.co.uk, waiting'],
            ]],
            ['Jobs and tenders', [
              ['Commercial jobs: title, description, location and deadline', 'Repaint reception, York YO1, by 20 Nov 2026'],
              ['Private tenders: title, description, location, deadline and estimated value', 'Office cleaning contract, York, by 1 Dec 2026, about £18,000'],
            ]],
            ['After a job', [
              ['Bids they receive', 'Five bids'],
              ['The work and visits', 'Started on 4 Nov 2026'],
              ['Invoices and payments', '£1,450, paid by card'],
              ['Reviews they leave', '5 stars'],
            ]],
            ['Safety', [
              ['Companies they block', 'One company'],
            ]],
          ]),
          record('Housing providers', [
            ['Company', [
              ['Name', 'Northfield Housing Trust'],
              ['Regulator number', 'RSH 4821'],
              ['When it was verified', '10 Oct 2026'],
              ['Whether it is connected to BedMaps', 'Yes'],
            ]],
            ['Team', [
              ['Members and their roles', 'Sam Ward, housing officer'],
              ['Invitations', 'maintenance@northfieldht.org.uk, waiting'],
            ]],
            ['Homes', [
              ['Name', 'Park Road House'],
              ['Address and postcode', '14 Park Road, Leeds LS6 2AB'],
              ['Unit reference', 'PR-02'],
              ['Their own reference', 'NHT-1182'],
            ]],
            ['Residents', [
              ['Residents they invite: name and email address', 'Amara Okafor, amara.okafor@gmail.com'],
              ['Requests from residents to link a home', 'Two waiting'],
              ['Repairs reported to them', 'Leaking kitchen tap, Flat 2'],
            ]],
            ['Safety checks', [
              ['What the check is', 'Gas safety check'],
              ['Which home', 'Park Road House'],
              ['Due date', '14 Jan 2027'],
              ['How often it repeats', 'Every 12 months'],
              ['When it was last done', '14 Jan 2026'],
              ['Which trade does it', 'Brightline Plumbing Ltd'],
            ]],
            ['Jobs and quotes', [
              ['Jobs they post for repairs and checks', 'Replace boiler pressure valve'],
              ['Quote approvals', '£480 quote, approved'],
              ['How much a quote can be before it needs approval', '£250'],
              ['Trades they work with', 'Brightline Plumbing Ltd'],
            ]],
            ['Rooms', [
              ['Rooms they list: address, rent, service charge, rules, support, access, photos and contact', 'Room 3, Park Road House, £95 a week plus £40 service charge, women 18+, step-free'],
              ['Their answers when we ask if a room is still free', 'Still free, 27 Oct 2026'],
              ['Applications they receive, and their decisions', 'Viewing offered'],
            ]],
            ['Referral documents', [
              ['Requests they make to see referral documents', 'Asked Amara for her ID and benefit letter'],
              ['Referral documents shared with them', 'Four shared'],
            ]],
            ['Links to their own systems', [
              ['Access key names', 'Housing system link'],
              ['The web address we send updates to', 'https://api.northfieldht.org.uk/repairs'],
              ['Failed sends', 'None this month'],
            ]],
          ]),
          record('Staff', [
            ['Role', [
              ['Admin or moderator', 'Moderator'],
              ['Who added them, and when', 'Added by an admin on 5 Oct 2026'],
            ]],
            ['What they do', [
              ['ID check decisions', 'Approved 12 checks'],
              ['Suspensions and other actions', 'Suspended one account for spam'],
              ['Which records they open', 'Opened Amara Okafor’s record on 28 Oct 2026'],
            ]],
          ]),
        ],
      },
      {
        key: 'referral-documents',
        name: 'Referral documents',
        summary: 'Legal and preferred name, date of birth, phone, email, current address and postcode, documents they upload with issue and expiry dates, who they share them with and why, requests to see them, and each time someone views them',
        records: [
          record('Profile', [
            ['Name', [
              ['Legal name', 'Amara Ngozi Okafor'],
              ['Preferred name', 'Amara'],
            ]],
            ['Date of birth', [
              ['Date of birth', '3 Mar 1994'],
              ['When it was locked, so it cannot be changed', '12 Oct 2026'],
            ]],
            ['Contact', [
              ['Phone number', '07700 900123'],
              ['Email address', 'amara.okafor@gmail.com'],
            ]],
            ['Address', [
              ['Current address', 'Flat 2, 14 Park Road, Leeds'],
              ['Current postcode', 'LS6 2AB'],
            ]],
          ]),
          record('Documents', [
            ['The document', [
              ['Kind of document', 'Proof of income'],
              ['Title', 'Universal Credit statement, Sept 2026'],
              ['The file', 'uc-statement-sept.pdf'],
              ['File type and size', 'PDF, 240 KB'],
            ]],
            ['Dates', [
              ['Issue date', '1 Sep 2026'],
              ['Expiry date', 'None'],
              ['When it was uploaded', '12 Oct 2026'],
              ['When it was removed', 'Not removed'],
            ]],
          ]),
          record('Shares', [
            ['Who and what', [
              ['The organisation it is shared with', 'Northfield Housing Trust'],
              ['Which parts', 'Profile and ID'],
              ['Which documents', 'Universal Credit statement'],
            ]],
            ['Why and how long', [
              ['Why it is shared', 'Room application'],
              ['When the share ends', '30 Nov 2026'],
              ['When it was stopped', 'Still active'],
              ['When it was last viewed', '16 Oct 2026'],
            ]],
          ]),
          record('Requests', [
            ['The request', [
              ['The organisation that asked', 'Parkside Lettings'],
              ['Who asked', 'Lucy Grant'],
              ['Which parts they asked for', 'ID and proof of income'],
              ['Why they asked', 'Tenancy check'],
            ]],
            ['The answer', [
              ['Whether the resident said yes or no', 'Yes'],
              ['When', '15 Oct 2026'],
            ]],
          ]),
          record('Views', [
            ['Who', [
              ['The person who viewed it', 'Sam Ward'],
              ['Their organisation', 'Northfield Housing Trust'],
            ]],
            ['What and when', [
              ['Which parts they saw', 'Profile and ID'],
              ['When', '16 Oct 2026, 14:02'],
            ]],
          ]),
        ],
      },
      {
        key: 'id-checks',
        name: 'ID checks',
        summary: 'Photos of their ID, the type of ID, the name and date of birth on it, the result and who checked it',
        records: [
          record('Each check', [
            ['What they send', [
              ['How they checked', 'Photos in the app'],
              ['Type of ID', 'Driving licence'],
              ['Photos of the ID', 'Front and back'],
              ['The date of birth they entered', '3 Mar 1994'],
            ]],
            ['What we read from it', [
              ['Name on the ID', 'Amara N Okafor'],
              ['Date of birth on the ID', '3 Mar 1994'],
            ]],
            ['The decision', [
              ['The result', 'Approved'],
              ['Who checked it', 'A staff moderator'],
              ['When', '13 Oct 2026'],
              ['Why it was rejected, if it was', 'Photo too blurry'],
              ['The checker’s note', '“Dates match”'],
            ]],
            ['Clearing up', [
              ['When the photos were deleted', '13 Nov 2026'],
            ]],
          ]),
          record('Stopping duplicates', [
            ['Fingerprints', [
              ['A scrambled fingerprint of each ID, so one ID cannot open two accounts', 'A string of letters and numbers that cannot be turned back into the ID'],
              ['Which account it belongs to', 'Amara Okafor'],
            ]],
          ]),
        ],
      },
      {
        key: 'housing-needs',
        name: 'Housing needs and history',
        summary: 'Benefits, gender, support needs, links to councils, access needs, consent to use health details, past addresses with move-in and move-out dates, tenancy type and landlord, and the kind of home and agreement they have now',
        records: [
          record('Housing profile', [
            ['Money', [
              ['Benefits', 'Universal Credit, PIP'],
            ]],
            ['About them', [
              ['Gender', 'Woman'],
            ]],
            ['Needs', [
              ['Support needs', 'Managing bills and benefit forms'],
              ['Access needs', 'Ground-floor room'],
            ]],
            ['Councils', [
              ['Councils they have a connection to', 'Leeds, Bradford'],
            ]],
            ['Consent', [
              ['When they agreed to us using health details', '12 Oct 2026'],
              ['When they finished the profile', '12 Oct 2026'],
            ]],
          ]),
          record('Past homes', [
            ['Where', [
              ['Address', '22 Elm Street, Bradford'],
              ['Postcode', 'BD3 9QT'],
            ]],
            ['When', [
              ['When they moved in', 'Mar 2021'],
              ['When they moved out', 'Aug 2024'],
            ]],
            ['The tenancy', [
              ['Tenancy type', 'Assured shorthold tenancy'],
              ['Landlord name', 'J Patel'],
              ['The housing provider, if it is on Resident Service 360', 'None'],
            ]],
            ['Notes', [
              ['Notes', '“Left when the landlord sold”'],
            ]],
          ]),
          record('Current home', [
            ['The home', [
              ['Country', 'England'],
              ['Kind of accommodation', 'Supported housing'],
            ]],
            ['The tenancy', [
              ['Tenancy type', 'Licence'],
              ['Kind of agreement', 'Licence agreement'],
            ]],
            ['Confirmed', [
              ['When they confirmed it', '12 Oct 2026'],
            ]],
          ]),
        ],
      },
      {
        key: 'enquiries',
        name: 'Housing enquiries',
        summary: 'Preferred areas, home size, who is moving, age group, gender, ethnic background (for equality monitoring only), current housing, when they need to move, support and access needs, benefits, work, study, savings, whether they can claim public funds, contact details and best time to call, anyone filling it in for them, and the consent they gave',
        records: [
          record('The home they need', [
            ['Where', [
              ['Preferred areas', 'Leeds, Wakefield'],
            ]],
            ['Size', [
              ['Largest property size', '1 bedroom'],
            ]],
            ['Who is moving', [
              ['Who the enquiry is for', 'Someone I’m helping'],
              ['Who is moving', 'Just me'],
              ['Number of adults', '1'],
              ['Number of children under 18', '0'],
            ]],
            ['Sharing', [
              ['Whether they would share a home', 'Yes, I would consider sharing'],
              ['Gender preference when sharing', 'Same gender only'],
              ['Age preference when sharing', 'No age preference'],
            ]],
          ]),
          record('About them', [
            ['Them', [
              ['Age group', '25 to 34'],
              ['Gender, or their own description', 'Man'],
            ]],
            ['Their situation', [
              ['Where they live now', 'Staying with friends or family'],
              ['When they need to move', 'As soon as possible'],
            ]],
          ]),
          record('Support and access', [
            ['Support', [
              ['Day-to-day support they need', 'Managing bills or benefit forms'],
              ['Other support', '“Help getting to appointments”'],
            ]],
            ['Access', [
              ['Access requirements', 'Step-free or ground-floor access'],
              ['Other access requirements', '“Room for a wheelchair to turn”'],
            ]],
          ]),
          record('Money', [
            ['Benefits', [
              ['Benefits they get', 'Universal Credit'],
              ['Any other benefit', 'Carer’s Allowance'],
              ['Whether their Universal Credit includes the extra for limited capability for work', 'Not sure'],
            ]],
            ['Work and study', [
              ['Whether they are in paid work', 'No'],
              ['Whether they are a full-time student', 'No'],
            ]],
            ['Savings', [
              ['Savings and investments', 'Up to £6,000'],
            ]],
            ['Public funds', [
              ['Whether they have been told they cannot claim public funds', 'No'],
            ]],
          ]),
          record('Contact', [
            ['Who', [
              ['Full name', 'Daniel Mensah'],
            ]],
            ['How', [
              ['Email address', 'daniel.mensah@outlook.com'],
              ['Phone number', '07700 900456'],
              ['How they prefer to be contacted', 'Phone call'],
            ]],
            ['When', [
              ['Whether we may leave a voicemail', 'Yes'],
              ['Best time to contact them', 'Weekday mornings'],
            ]],
          ]),
          record('Someone helping', [
            ['The helper', [
              ['Name of the person filling it in', 'Grace Mensah'],
              ['Their relationship to the person', 'Sister'],
            ]],
            ['Permission', [
              ['Whether they have the person’s permission', 'Yes'],
            ]],
          ]),
          record('Equality monitoring', [
            ['Kept apart from the enquiry', [
              ['Ethnic background', 'Black, Black British, Caribbean or African'],
              ['Their own description of it', 'Ghanaian'],
            ]],
          ]),
          record('Consent', [
            ['What they agreed to', [
              ['The privacy notice version they saw', '17 Oct 2026'],
              ['The consent wording', 'Consent to us using their information, including benefit, health and support details'],
            ]],
            ['When', [
              ['When they consented', '21 Oct 2026, 19:40'],
            ]],
            ['Handling', [
              ['Where the enquiry is up to', 'New'],
              ['When staff were alerted', '21 Oct 2026, 19:41'],
              ['When it will be deleted', '21 Apr 2027'],
            ]],
          ]),
        ],
      },
      {
        key: 'repairs',
        name: 'Homes and repairs',
        summary: 'Their homes and addresses, the housing provider they are linked to, each repair (what is wrong, how urgent, status, dates), photos, updates, landlord or agent contact details and whether we may contact them, feedback, and what our photo checker made of the photos',
        records: [
          record('Homes', [
            ['The home', [
              ['A name for it', 'Home'],
              ['Address', 'Flat 2, 14 Park Road, Leeds'],
              ['Postcode', 'LS6 2AB'],
            ]],
            ['Provider', [
              ['The housing provider it is linked to', 'Northfield Housing Trust'],
              ['The provider’s own record for it', 'Park Road House, unit PR-02'],
            ]],
            ['Archive', [
              ['When it was archived', 'Not archived'],
            ]],
          ]),
          record('Linking to a provider', [
            ['The request', [
              ['Which provider', 'Northfield Housing Trust'],
              ['The home’s name, address and postcode at the time', 'Home, Flat 2, 14 Park Road, LS6 2AB'],
              ['A message from the resident', '“I moved in on 1 October”'],
            ]],
            ['The answer', [
              ['The provider’s note', '“Confirmed, welcome”'],
              ['The outcome', 'Approved'],
              ['Who decided, and when', 'Sam Ward, 14 Oct 2026'],
            ]],
          ]),
          record('Landlord or agent', [
            ['Who', [
              ['Landlord or agency', 'Agency'],
              ['Landlord name', 'J Patel'],
              ['Agency name', 'Parkside Lettings'],
              ['Agency contact name', 'Tom Reid'],
            ]],
            ['Contact', [
              ['Email address', 'tom@parksidelettings.co.uk'],
              ['Phone number', '0113 496 0000'],
            ]],
            ['Permission', [
              ['Whether we may contact them', 'Yes'],
              ['When they said so', '12 Oct 2026'],
            ]],
          ]),
          record('Repairs', [
            ['The problem', [
              ['Title', 'Leaking kitchen tap'],
              ['What is wrong', '“Water drips under the sink when the tap is on”'],
              ['How urgent it is', 'Urgent'],
            ]],
            ['Progress', [
              ['Status', 'Booked'],
              ['When it was reported', '27 Oct 2026'],
              ['When it is booked in', '30 Oct 2026, 9am'],
              ['When to follow up', '3 Nov 2026'],
            ]],
            ['Sending it', [
              ['Who it was sent to', 'Northfield Housing Trust'],
              ['How it was sent', 'Email'],
              ['Whether the email arrived', 'Delivered'],
            ]],
          ]),
          record('Updates', [
            ['Who', [
              ['Who updated it', 'Sam Ward, Northfield Housing Trust'],
            ]],
            ['What', [
              ['What changed', 'Booked a plumber'],
              ['The new status', 'Booked'],
              ['Their message', '“Plumber coming Thursday morning”'],
              ['The booked date', '30 Oct 2026'],
            ]],
          ]),
          record('Photos', [
            ['The photo', [
              ['The file', 'sink-leak.jpg'],
              ['File type and size', 'JPEG, 1.8 MB'],
              ['Width and height', '3024 by 4032'],
              ['Caption', '“Under the sink”'],
            ]],
            ['Dates', [
              ['When it was added', '27 Oct 2026'],
              ['When it was removed, and by whom', 'Not removed'],
            ]],
          ]),
          record('Photo checker', [
            ['What it looked at', [
              ['Which photos', 'sink-leak.jpg'],
              ['How many', '2'],
            ]],
            ['What it found', [
              ['Its findings', 'Leak at the tap joint, likely a worn washer, urgent'],
              ['When', '27 Oct 2026'],
            ]],
          ]),
          record('Feedback', [
            ['Feedback', [
              ['The message', '“Fixed quickly, thank you”'],
              ['Which repair', 'Leaking kitchen tap'],
              ['When', '31 Oct 2026'],
            ]],
          ]),
        ],
      },
      {
        key: 'rooms',
        name: 'Rooms and applications',
        summary: 'Rooms providers list (address, rent, service charge, benefits accepted, age and gender rules, support offered, access features, photos, contact), applications (message, what the resident chose to share, the decision and why), saved rooms, and providers’ answers when we ask if a room is still free',
        records: [
          record('Rooms', [
            ['Where', [
              ['Address, town and postcode', '14 Park Road, Leeds, LS6 2AB'],
              ['Map position', 'Near Hyde Park, Leeds'],
              ['Council', 'Leeds'],
            ]],
            ['The room', [
              ['Kind of home', 'Shared house'],
              ['Room number', 'Room 3'],
              ['How many rooms are in the house', '5'],
              ['Floor', 'Ground'],
              ['En-suite', 'No'],
              ['Furnished', 'Yes'],
              ['Description', '“Bright room near the bus stop, shared kitchen”'],
              ['Photos', 'Four photos'],
            ]],
            ['Money', [
              ['Weekly rent', '£95'],
              ['Weekly service charge', '£40'],
              ['Who pays the rent', 'Housing Benefit'],
              ['Benefits accepted', 'Universal Credit, Housing Benefit'],
            ]],
            ['Who it suits', [
              ['Age range', '18 to 35'],
              ['Gender rule', 'Women only'],
              ['Support needs it is for', 'Moving on from homelessness'],
              ['Councils it takes referrals from', 'Leeds'],
              ['Access features', 'Step-free entrance'],
            ]],
            ['Availability', [
              ['Available from', '1 Nov 2026'],
              ['When availability was last confirmed', '27 Oct 2026'],
              ['When it was published', '20 Oct 2026'],
            ]],
            ['Contact', [
              ['Contact name', 'Sam Ward'],
              ['Contact email', 'lettings@northfieldht.org.uk'],
            ]],
          ]),
          record('Is it still free?', [
            ['Asking', [
              ['When we asked the provider', '27 Oct 2026'],
              ['When we reminded them', '29 Oct 2026'],
            ]],
            ['The answer', [
              ['Their answer', 'Still free'],
              ['When', '28 Oct 2026'],
              ['When the question expired', 'Answered in time'],
              ['How many checks went unanswered', '0'],
            ]],
          ]),
          record('Applications', [
            ['What they send', [
              ['Which room', 'Room 3, Park Road House'],
              ['Their message', '“I work nights, so a quiet room suits me”'],
            ]],
            ['What they share', [
              ['Whether they shared their housing profile', 'Yes'],
              ['Which parts', 'Profile and ID'],
              ['Which documents', 'Universal Credit statement'],
            ]],
            ['The decision', [
              ['Status, and each change to it', 'Sent, then seen, then viewing offered'],
              ['When it was sent, seen and decided', 'Sent 21 Oct, seen 22 Oct, decided 23 Oct'],
              ['Why it was declined', 'Room already taken'],
              ['When it was withdrawn', 'Not withdrawn'],
            ]],
          ]),
          record('Saved rooms', [
            ['Saved', [
              ['The rooms each resident saved', 'Room 3, Park Road House'],
              ['When', '19 Oct 2026'],
            ]],
          ]),
        ],
      },
      {
        key: 'providers',
        name: 'Housing providers',
        summary: 'Their homes and unit references, residents they invite (name and email), planned safety checks and when they were done, quote approvals, and the trades they work with',
        records: [
          record('Homes they manage', [
            ['The home', [
              ['Name', 'Park Road House'],
              ['Address', '14 Park Road, Leeds'],
              ['Postcode', 'LS6 2AB'],
            ]],
            ['References', [
              ['Unit reference', 'PR-02'],
              ['Their own reference', 'NHT-1182'],
            ]],
            ['Housekeeping', [
              ['Who added it', 'Sam Ward'],
              ['When it was archived', 'Not archived'],
            ]],
          ]),
          record('Residents they invite', [
            ['The invite', [
              ['Resident name', 'Amara Okafor'],
              ['Email address', 'amara.okafor@gmail.com'],
              ['Which home', 'Park Road House, PR-02'],
              ['Who invited them', 'Sam Ward'],
            ]],
            ['Outcome', [
              ['Status', 'Linked'],
              ['When the resident linked up', '13 Oct 2026'],
            ]],
          ]),
          record('Planned checks', [
            ['The check', [
              ['What it is', 'Gas safety check'],
              ['Which home', 'Park Road House'],
              ['Kind of job', 'Gas safety certificate'],
            ]],
            ['Timing', [
              ['Due date', '14 Jan 2027'],
              ['How often it repeats', 'Every 12 months'],
              ['When it was last done', '14 Jan 2026'],
            ]],
            ['Who does it', [
              ['Which trade it is given to', 'Brightline Plumbing Ltd'],
            ]],
            ['Progress', [
              ['Status', 'Booked'],
              ['Notes', '“Tenant works days, book after 4pm”'],
              ['Each time it was done or changed', 'Done on 14 Jan 2026, certificate added'],
            ]],
          ]),
          record('Quote approvals', [
            ['The request', [
              ['Which quote', '£480 to replace a boiler valve'],
              ['Who asked', 'Sam Ward'],
              ['Their note', '“Over our limit, please approve”'],
            ]],
            ['The decision', [
              ['Approved or not', 'Approved'],
              ['Who decided', 'Head of Housing'],
              ['When', '22 Oct 2026'],
              ['A note on the decision', '“Go ahead”'],
            ]],
          ]),
          record('Trades they work with', [
            ['The trade', [
              ['The company, or the email and company name they invited', 'Brightline Plumbing Ltd'],
              ['Status', 'Active'],
              ['Who added it', 'Sam Ward'],
            ]],
          ]),
        ],
      },
      {
        key: 'companies',
        name: 'Companies',
        summary: 'Company name, type, country, registration and regulator numbers, the Companies House check, description, trades, areas served, accreditations, team members and their roles, invoice name and address, payout accounts, subscriptions, and links to their own systems',
        records: [
          record('Company', [
            ['Who they are', [
              ['Name', 'Brightline Plumbing Ltd'],
              ['Type: trade, buyer or housing provider', 'Trade'],
              ['Description', '“Plumbing and heating across West Yorkshire”'],
            ]],
            ['Registration', [
              ['Country', 'United Kingdom'],
              ['Country of registration', 'England and Wales'],
              ['Registration number', '12345678'],
              ['Regulator number', 'Gas Safe 654321'],
              ['When it was verified', '15 Oct 2026'],
            ]],
            ['What they do', [
              ['Trades', 'Plumbing, heating'],
              ['Areas served', 'Leeds, Bradford, Wakefield'],
            ]],
            ['Settings', [
              ['How much a quote can be before it needs approval', '£250'],
              ['Whether it is connected to BedMaps', 'No'],
            ]],
          ]),
          record('Companies House check', [
            ['The check', [
              ['The number checked', '12345678'],
              ['The name found', 'BRIGHTLINE PLUMBING LTD'],
              ['Company status', 'Active'],
              ['The result', 'Match'],
              ['A note', '“Director name matches”'],
              ['Who checked', 'A staff admin'],
              ['When', '15 Oct 2026'],
            ]],
          ]),
          record('Accreditations', [
            ['Each accreditation', [
              ['Which accreditation', 'Gas Safe'],
              ['Reference number', '654321'],
              ['Who added it', 'Dan Hughes'],
            ]],
          ]),
          record('Team', [
            ['Members', [
              ['Who they are', 'Priya Shah'],
              ['Their role', 'Engineer'],
              ['Whether they are active', 'Active'],
              ['Who invited them', 'Dan Hughes'],
            ]],
            ['Invitations', [
              ['Email address', 'priya@brightlineplumbing.co.uk'],
              ['Role', 'Engineer'],
              ['When it was sent', '16 Oct 2026'],
              ['Accepted or cancelled', 'Accepted on 17 Oct 2026'],
            ]],
            ['Changes', [
              ['A log of team changes', 'Dan made Priya an admin on 20 Oct 2026'],
            ]],
          ]),
          record('Where they work from', [
            ['Base', [
              ['Postcode area', 'LS11'],
              ['Rough map position', 'Near Leeds city centre'],
              ['Who set it', 'Dan Hughes'],
            ]],
          ]),
          record('Invoices and payouts', [
            ['Invoice details', [
              ['Legal name', 'Brightline Plumbing Ltd'],
              ['Legal address', '5 Mill Lane, Leeds LS11 5AA'],
              ['Company number', '12345678'],
            ]],
            ['Payouts', [
              ['Payout account', 'Stripe account ending 4821'],
              ['Its country', 'United Kingdom'],
            ]],
          ]),
          record('Subscriptions', [
            ['Plan', [
              ['Apple or card subscription', 'Card, monthly'],
              ['Valid until', '30 Nov 2026'],
              ['When it was cancelled', 'Not cancelled'],
            ]],
            ['History', [
              ['Each change to it', 'Renewed on 30 Oct 2026'],
              ['Each bid counted against their allowance', 'Bid on the leaking tap job, 28 Oct 2026'],
            ]],
          ]),
          record('Links to their own systems', [
            ['Access keys', [
              ['Name', 'Housing system link'],
              ['When it was made', '10 Oct 2026'],
              ['When it was last used', '28 Oct 2026'],
              ['When it was cancelled', 'Still active'],
            ]],
            ['Updates we send', [
              ['The web address', 'https://api.northfieldht.org.uk/repairs'],
              ['Whether it is on', 'On'],
              ['Failed sends', '0'],
              ['Last success and last failure', 'Worked on 28 Oct 2026, no failures'],
            ]],
          ]),
        ],
      },
      {
        key: 'jobs',
        name: 'Jobs and work',
        summary: 'Jobs posted (what, where, exact address, deadline, budget), bids and prices, work agreed and how it is going, visit times, invoices, payments and refunds, reviews and replies, saved jobs and searches, job alerts, and rough locations for distance search',
        records: [
          record('Home jobs', [
            ['The job', [
              ['Title', 'Replace bathroom extractor fan'],
              ['Description', '“Fan is noisy and has stopped working”'],
              ['Category', 'Electrical'],
              ['Kind of job', 'Extractor fan repair'],
            ]],
            ['Where', [
              ['Area', 'LS6'],
              ['Exact address', 'Flat 2, 14 Park Road, Leeds'],
            ]],
            ['When', [
              ['Deadline', '15 Nov 2026'],
              ['Status', 'Open'],
            ]],
          ]),
          record('Commercial jobs', [
            ['The job', [
              ['Title', 'Repaint reception'],
              ['Description', '“Two walls and a ceiling, about 40 square metres”'],
              ['Category', 'Decorating'],
              ['Kind of job', 'Interior painting'],
            ]],
            ['Where', [
              ['Location', 'York YO1'],
            ]],
            ['When', [
              ['Deadline', '20 Nov 2026'],
              ['Status', 'Open'],
            ]],
            ['What it is for', [
              ['The home, repair or planned check', 'Gas safety check at Park Road House'],
            ]],
          ]),
          record('Private tenders', [
            ['The tender', [
              ['Title', 'Office cleaning contract'],
              ['Description', '“Daily cleaning for two floors”'],
              ['Category', 'Cleaning'],
            ]],
            ['Where', [
              ['Location', 'York'],
            ]],
            ['Money', [
              ['Estimated value', '£18,000'],
              ['Currency', 'Pounds'],
            ]],
            ['When', [
              ['Deadline', '1 Dec 2026'],
              ['Status', 'Open'],
            ]],
          ]),
          record('Bids', [
            ['The bid', [
              ['Message', '“Can attend Thursday morning”'],
              ['Price', '£120'],
              ['Who sent it', 'Dan Hughes, Brightline Plumbing Ltd'],
            ]],
            ['Outcome', [
              ['Status', 'Accepted'],
              ['When', '28 Oct 2026'],
            ]],
          ]),
          record('Work', [
            ['The work', [
              ['What it covers', 'Replace kitchen tap washer'],
              ['Agreed amount', '£120'],
            ]],
            ['Progress', [
              ['Status', 'Finished'],
              ['When it started', '30 Oct 2026'],
              ['When it was handed in', '30 Oct 2026'],
              ['When it was finished', '31 Oct 2026'],
              ['When it was cancelled', 'Not cancelled'],
            ]],
            ['Problems', [
              ['Follow-ups', '0'],
              ['Requests to cancel', 'None'],
            ]],
            ['History', [
              ['Each change of status, with a note and photos', 'Started, with a before photo'],
            ]],
          ]),
          record('Visits', [
            ['Planning', [
              ['Why the visit is needed', 'Fix the leak'],
              ['Time slots offered', 'Thu 30 Oct 9am to 11am, Fri 31 Oct 1pm to 3pm'],
              ['Time zone', 'London'],
            ]],
            ['Agreed', [
              ['The time agreed', 'Thu 30 Oct, 9am to 11am'],
              ['Who agreed it', 'Amara Okafor'],
              ['When', '28 Oct 2026'],
            ]],
            ['Changes', [
              ['Who cancelled, and why', 'Nobody'],
              ['Rearranged visits', 'None'],
            ]],
          ]),
          record('Invoices', [
            ['The invoice', [
              ['Title', 'Kitchen tap repair'],
              ['What it covers', 'New washer and labour'],
              ['Amount', '£120'],
            ]],
            ['Seller', [
              ['Legal name', 'Brightline Plumbing Ltd'],
              ['Address', '5 Mill Lane, Leeds LS11 5AA'],
              ['Company number', '12345678'],
              ['Country', 'United Kingdom'],
            ]],
            ['Payment', [
              ['Paid by card or cash', 'Card'],
              ['Payment status', 'Paid'],
              ['When it was paid', '31 Oct 2026'],
              ['Receipt link', 'A Stripe receipt'],
            ]],
            ['Problems', [
              ['Refunds', 'None'],
              ['Disputes', 'None'],
            ]],
          ]),
          record('Reviews', [
            ['The review', [
              ['Star rating', '5'],
              ['Review text', '“Fixed quickly and tidied up”'],
              ['Edits', '0'],
            ]],
            ['Reply', [
              ['The company’s reply', '“Thanks, glad it is sorted”'],
            ]],
            ['Moderation', [
              ['Whether staff hid it', 'No'],
            ]],
          ]),
          record('Saved and alerts', [
            ['Saved', [
              ['Saved jobs', 'Repaint reception, York'],
            ]],
            ['Searches', [
              ['Name', 'Plumbing near me'],
              ['Kinds of job', 'Home jobs'],
              ['Trades', 'Plumbing'],
              ['Keywords', 'leak, boiler'],
              ['Base area', 'LS11'],
              ['Distance', '10 miles'],
              ['Budget', '£50 to £500'],
              ['Alerts on or off', 'On'],
            ]],
            ['Alerts', [
              ['Job alerts sent', '“New plumbing job 2 miles away”'],
            ]],
          ]),
          record('Locations', [
            ['Jobs', [
              ['Rough map position of each job’s area', 'Near Hyde Park, Leeds'],
            ]],
            ['Trades', [
              ['Rough position of each trade, for distance search', 'Near Leeds city centre'],
            ]],
          ]),
          record('Change history', [
            ['Changes', [
              ['Which details of a job changed', 'Deadline and description'],
              ['Who changed them', 'Amara Okafor'],
              ['When', '29 Oct 2026'],
            ]],
          ]),
        ],
      },
      {
        key: 'messages',
        name: 'Messages',
        summary: 'Messages between customers and companies, when they were read, and the email or phone number people choose to share',
        records: [
          record('Conversations', [
            ['Who', [
              ['The customer', 'Amara Okafor'],
              ['The buyer or housing provider', 'Northfield Housing Trust'],
              ['The trade', 'Brightline Plumbing Ltd'],
            ]],
            ['About', [
              ['The job, bid or work', 'Leaking kitchen tap'],
            ]],
            ['Activity', [
              ['When the last message was sent', '29 Oct 2026, 18:22'],
            ]],
          ]),
          record('Messages', [
            ['The message', [
              ['Text', '“Can you come before 10am?”'],
              ['Who sent it', 'Amara Okafor'],
              ['When', '29 Oct 2026, 18:22'],
            ]],
            ['Moderation', [
              ['Whether staff hid it', 'No'],
            ]],
          ]),
          record('Read', [
            ['Read', [
              ['When each person last read the conversation', 'Dan Hughes, 29 Oct 2026, 18:40'],
            ]],
          ]),
          record('Shared contact details', [
            ['Shared', [
              ['Email address', 'amara.okafor@gmail.com'],
              ['Phone number', '07700 900123'],
              ['When they shared it', '29 Oct 2026'],
              ['When they stopped', 'Still shared'],
            ]],
          ]),
        ],
      },
      {
        key: 'safety',
        name: 'Safety',
        summary: 'Reports about content or people, blocks, suspensions and why, appeals and decisions, actions staff took, and which staff opened whose records',
        records: [
          record('Reports', [
            ['The report', [
              ['Who reported', 'Amara Okafor'],
              ['What they reported', 'A message'],
              ['Reason', 'Abusive'],
              ['Details', '“Swore at me after I turned down the bid”'],
            ]],
            ['Outcome', [
              ['Status', 'Action taken'],
            ]],
          ]),
          record('Blocks', [
            ['Blocks', [
              ['Who blocked whom', 'Amara Okafor blocked Quickfix Repairs'],
              ['People or companies', 'Company'],
              ['When', '29 Oct 2026'],
            ]],
          ]),
          record('Suspensions', [
            ['The suspension', [
              ['The person or company', 'Quickfix Repairs'],
              ['Reason', 'Abusive messages'],
              ['Start', '29 Oct 2026'],
              ['End', '5 Nov 2026'],
              ['Who suspended them', 'A staff moderator'],
            ]],
            ['Lifting it', [
              ['Who lifted it', 'A staff admin'],
              ['Why', 'Appeal accepted'],
              ['When', '2 Nov 2026'],
            ]],
          ]),
          record('Appeals', [
            ['The appeal', [
              ['Their statement', '“It was a misunderstanding, I have apologised”'],
              ['Which suspension or action', '7-day suspension from 29 Oct 2026'],
            ]],
            ['The decision', [
              ['The decision', 'Accepted'],
              ['Who decided', 'A staff admin'],
              ['A note', '“First warning”'],
              ['When', '2 Nov 2026'],
            ]],
          ]),
          record('Staff actions', [
            ['Each action', [
              ['What staff did', 'Hid a message'],
              ['Reason', 'Abusive'],
              ['Details', '“Matched a report”'],
              ['Who did it', 'A staff moderator'],
              ['When', '29 Oct 2026'],
            ]],
          ]),
          record('Record views', [
            ['Each view', [
              ['Which staff member', 'A staff moderator'],
              ['Whose record', 'Amara Okafor'],
              ['Whether it held sensitive details', 'Yes, ID photos'],
              ['When', '13 Oct 2026'],
            ]],
          ]),
        ],
      },
      {
        key: 'tenders',
        name: 'Government tenders',
        summary: 'Public tender notices and contract awards we gather, and companies’ responses and checklists for them',
        records: [
          record('Tender notices', [
            ['The notice, from public sites', [
              ['Title', 'Responsive repairs for council homes'],
              ['Description', '“Plumbing, electrical and joinery repairs across 3,000 homes”'],
              ['Buyer', 'A city council'],
              ['Stage and status', 'Tender, open'],
            ]],
            ['Money', [
              ['Value', '£2,400,000'],
              ['Currency', 'Pounds'],
            ]],
            ['When', [
              ['Published', '10 Oct 2026'],
              ['Deadline', '20 Nov 2026'],
              ['Last updated', '15 Oct 2026'],
            ]],
            ['What and where', [
              ['Kinds of work', 'Repair and maintenance'],
              ['Regions', 'Yorkshire and the Humber'],
              ['Place', 'Leeds'],
              ['Country', 'United Kingdom'],
            ]],
            ['Links', [
              ['The notice', 'Find a Tender page'],
              ['Where to apply', 'The council’s supplier portal'],
              ['Documents', 'Specification and pricing sheet'],
            ]],
            ['Details', [
              ['Requirements', '£5m public liability insurance'],
              ['Number of lots', '3'],
              ['Whether it suits small firms', 'Yes'],
            ]],
          ]),
          record('Contract awards', [
            ['The award, from public sites', [
              ['Title', 'Gas servicing contract'],
              ['Buyer', 'A housing association'],
              ['Supplier', 'A heating firm'],
              ['Value', '£650,000'],
              ['Award date', '2 Sep 2026'],
            ]],
            ['What', [
              ['Kinds of work', 'Heating servicing'],
            ]],
            ['Link', [
              ['Link to the award', 'Contracts Finder page'],
            ]],
          ]),
          record('Responses', [
            ['The response', [
              ['Which company', 'Brightline Plumbing Ltd'],
              ['Which notice', 'Responsive repairs for council homes'],
              ['Status', 'In progress'],
              ['Notes', '“Apply for lot 1 only”'],
            ]],
            ['Sending it', [
              ['Submission reference', 'BID-2026-118'],
              ['When they said they sent it', '18 Nov 2026'],
              ['Who said so', 'Dan Hughes'],
            ]],
          ]),
          record('Checklists', [
            ['Each step', [
              ['The step', 'Upload insurance certificate'],
              ['Detail', '“£5m public liability”'],
              ['Where it came from', 'The notice’s requirements'],
            ]],
            ['Done', [
              ['Whether it is done', 'Done'],
              ['When', '12 Nov 2026'],
              ['By whom', 'Priya Shah'],
            ]],
          ]),
        ],
      },
      {
        key: 'outreach',
        name: 'Outreach and research',
        summary: 'Housing providers we research (name, website, email, phone, office, areas, who they support, ages, room types, how to refer, team), their replies (taking referrals or not, contact person, requirements), our email schedule, trades we invite and whether emails were opened or bounced, people who asked not to be emailed, council housing contacts, sponsorship and investor emails, and investor and funding research',
        records: [
          record('Housing providers we research', [
            ['Contact', [
              ['Organisation', 'Northfield Housing Trust'],
              ['Website', 'northfieldht.org.uk'],
              ['Email address', 'info@northfieldht.org.uk'],
              ['Phone number', '0113 496 0123'],
              ['Office address and postcode', '3 Station Road, Leeds LS1 4AB'],
            ]],
            ['Where they work', [
              ['Nations', 'England'],
              ['Areas served', 'West Yorkshire'],
              ['Councils they work in', 'Leeds, Wakefield'],
            ]],
            ['Who they help', [
              ['Who they support', 'Young people moving on from homelessness'],
              ['Gender focus', 'Women'],
              ['Age range', '18 to 25'],
              ['A summary of their support', '“Shared houses with weekly key work”'],
            ]],
            ['Housing', [
              ['Kinds of accommodation', 'Shared houses'],
              ['Kinds of rooms', 'Single rooms'],
              ['Rooms listed', '12'],
            ]],
            ['Referrals', [
              ['Whether people can refer themselves', 'Yes'],
              ['How to refer', 'Online form'],
              ['Referral form', 'Their referral form'],
              ['Referral web page', 'northfieldht.org.uk/refer'],
            ]],
            ['Their team', [
              ['Team page', 'northfieldht.org.uk/team'],
              ['People', 'Sam Ward, housing officer'],
            ]],
            ['Our notes', [
              ['Priority', 'High'],
              ['When we last contacted them', '20 Oct 2026'],
              ['Why they are left out of outreach, if they are', 'A refuge, kept off the site'],
            ]],
          ]),
          record('Their replies', [
            ['The answer', [
              ['Taking referrals, no spaces, or said no', 'Taking referrals'],
              ['When they replied', '29 Oct 2026'],
            ]],
            ['Contact', [
              ['Referral email address', 'referrals@northfieldht.org.uk'],
              ['Contact name', 'Sam Ward'],
              ['Role', 'Housing officer'],
              ['Email address', 'sam.ward@northfieldht.org.uk'],
              ['Phone number', '0113 496 0123'],
            ]],
            ['Details', [
              ['Their requirements', 'A local connection to Leeds'],
              ['A note', '“Rooms free now”'],
            ]],
          ]),
          record('Outreach schedule', [
            ['Schedule', [
              ['Each email in the sequence', 'First email, then a follow-up'],
              ['When it is due', '3 Nov 2026'],
              ['Status', 'Waiting'],
            ]],
          ]),
          record('Trades we invite', [
            ['The business', [
              ['Business name', 'Quickfix Repairs'],
              ['Website', 'quickfixrepairs.co.uk'],
              ['Email address', 'hello@quickfixrepairs.co.uk'],
              ['Company number', '11223344'],
            ]],
            ['Choosing', [
              ['Why they were picked or skipped', 'Close to the job and well reviewed'],
            ]],
            ['The invite', [
              ['Status', 'Sent'],
              ['Whether emails arrived, were opened or bounced', 'Opened'],
              ['Whether they joined', 'Joined on 25 Oct 2026'],
            ]],
          ]),
          record('Do not contact', [
            ['The list', [
              ['Email addresses and websites that asked not to be emailed', 'office@greenlanejoinery.co.uk'],
              ['Why', 'Asked to unsubscribe'],
            ]],
          ]),
          record('Council housing contacts', [
            ['The council', [
              ['Council', 'Ashby Borough Council'],
              ['Organisation', 'Housing options team'],
            ]],
            ['Contact', [
              ['Email address', 'housing@ashby.gov.uk'],
              ['Phone number', '01632 960000'],
              ['Office address', 'Town Hall, Ashby'],
            ]],
            ['Web', [
              ['Housing web page', 'ashby.gov.uk/housing'],
              ['Website', 'ashby.gov.uk'],
            ]],
            ['Notes', [
              ['A note', '“Ask for the single homeless team”'],
              ['When we checked it', '18 Oct 2026'],
            ]],
          ]),
          record('Sponsorship and investor emails', [
            ['Each email', [
              ['Organisation', 'Harbour Ventures'],
              ['Sent or received', 'Received'],
              ['Address', 'partners@harbourventures.co.uk'],
              ['Sender name', 'Leah Cole'],
              ['Subject', '“Re: Resident Service 360”'],
              ['The email itself', '“Happy to talk next week”'],
              ['When', '24 Oct 2026'],
            ]],
          ]),
          record('Investor and funding research', [
            ['Research', [
              ['Investors', 'Harbour Ventures, early stage, housing focus'],
              ['Grants and loans', 'Community housing grant, up to £50,000'],
            ]],
          ]),
          record('Emails we send', [
            ['Each email', [
              ['Invitations and room emails', 'Invitation to bid on a job'],
              ['Who to', 'hello@quickfixrepairs.co.uk'],
              ['Subject', '“A job near you: leaking kitchen tap”'],
              ['When it was sent', '27 Oct 2026'],
              ['Any error', 'None'],
            ]],
          ]),
        ],
      },
    ],
  },
  {
    key: 'bedmaps',
    brand: 'BedMaps',
    categories: [
      {
        key: 'listings',
        name: 'Property listings',
        section: 'bedmaps.com',
        summary: 'Who is listing (landlord, managing agent or supported housing provider), their name, organisation, email and phone, the address and council, property type, shared facilities, step-free access, furnished or not, date available, weekly rent per room, which safety certificates and licence they hold, each room (type, size, floor, en-suite, furnished), photos, and when they consented',
        records: [
          record('Who is listing', [
            ['Role', [
              ['Landlord, managing agent or supported housing provider', 'Managing agent'],
            ]],
            ['Contact', [
              ['Contact name', 'Tom Reid'],
              ['Organisation', 'Parkside Lettings'],
              ['Email address', 'tom@parksidelettings.co.uk'],
              ['Phone number', '0113 496 0000'],
            ]],
          ]),
          record('The property', [
            ['Where', [
              ['Address', '9 Victoria Terrace'],
              ['Town', 'Leeds'],
              ['Postcode', 'LS3 1BX'],
              ['Council', 'Leeds'],
              ['Map position', 'Near Burley, Leeds'],
            ]],
            ['The building', [
              ['Property type: house, flat, HMO or block of flats', 'HMO'],
              ['Shared facilities: kitchen, bathrooms, living room, garden, laundry', 'Kitchen, bathrooms, laundry'],
              ['Access: step-free entrance, ground-floor rooms, adapted bathroom', 'Ground-floor rooms'],
              ['Furnished', 'Yes'],
            ]],
            ['Letting', [
              ['Available from', '15 Nov 2026'],
              ['Weekly rent per room', '£110'],
            ]],
            ['Paperwork', [
              ['Documents they can provide: gas safety, electrical safety, energy certificate, HMO licence', 'Gas safety, energy certificate, HMO licence'],
            ]],
          ]),
          record('Rooms', [
            ['Each room', [
              ['Name', 'Room 1'],
              ['Type: single, double or studio', 'Double'],
              ['Size', '12 square metres'],
              ['Floor', 'Ground'],
              ['En-suite', 'Yes'],
              ['Furnished', 'Yes'],
            ]],
          ]),
          record('Photos', [
            ['Each photo', [
              ['The file', 'room-1.jpg'],
              ['The room it shows', 'Room 1'],
              ['Its order', 'First'],
              ['File type and size', 'JPEG, 2.1 MB'],
            ]],
          ]),
          record('Consent', [
            ['Consent', [
              ['When they consented', '26 Oct 2026, 10:12'],
            ]],
            ['Handling', [
              ['Status', 'New'],
              ['When we were told about it', '26 Oct 2026, 10:13'],
            ]],
          ]),
        ],
      },
      {
        key: 'contact',
        name: 'Contact form',
        section: 'bedmaps.com',
        summary: 'Whether they are a provider, council, referral team, landlord or partner, their name, email and organisation, what it is about, and their message',
        records: [
          record('Who they are', [
            ['Role', [
              ['A provider, a council, a referral team, a landlord or a partner', 'A referral team'],
            ]],
          ]),
          record('Their details', [
            ['Name', [
              ['Name', 'Hannah Price'],
            ]],
            ['Contact', [
              ['Email address', 'hannah.price@ashby.gov.uk'],
              ['Organisation', 'Ashby Borough Council'],
            ]],
          ]),
          record('What it is about', [
            ['Topics', [
              ['Licensing, vacancies and availability, moves between areas, landlord returns, or something else', 'Vacancies and availability'],
            ]],
            ['Message', [
              ['Their message', '“Do you have rooms for young women in Leeds?”'],
            ]],
          ]),
        ],
      },
      {
        key: 'find-housing',
        name: 'Find Housing providers',
        section: 'bedmaps.com',
        summary: 'The providers we list: name, area, who they support, a short summary, whether they take referrals, how to refer, website and email',
        records: [
          record('Each listing', [
            ['Who', [
              ['Name', 'Park Road House'],
              ['Provider', 'Northfield Housing Trust'],
            ]],
            ['Where', [
              ['Area', 'Hyde Park'],
              ['Town', 'Leeds'],
              ['Street', 'Park Road'],
              ['Postcode area', 'LS6'],
              ['Council', 'Leeds'],
              ['Map position', 'Near Hyde Park, Leeds'],
            ]],
            ['Support', [
              ['Who they support', 'Moving on from homelessness'],
              ['A summary of their support', '“Shared rooms with weekly key work”'],
              ['Help with money', 'Help claiming benefits'],
            ]],
            ['The home', [
              ['Property type', 'Shared house'],
              ['Rooms', '5'],
              ['Access', 'Step-free entrance'],
              ['Features', 'Garden, laundry'],
              ['Pets allowed', 'No'],
              ['Photo', 'A photo of the house'],
            ]],
            ['Spaces', [
              ['Spaces now', '2'],
              ['Spaces soon', '1'],
              ['Waiting list', 'No'],
            ]],
          ]),
          record('Referrals', [
            ['Taking referrals', [
              ['Whether they are taking referrals', 'Accepting referrals'],
              ['How to refer', '“Email their referral form to referrals@northfieldht.org.uk”'],
            ]],
            ['Contact', [
              ['Website', 'northfieldht.org.uk'],
              ['Email address', 'referrals@northfieldht.org.uk'],
            ]],
          ]),
        ],
      },
      {
        key: 'app-residents',
        name: 'Residents',
        section: 'BedMaps app',
        summary: 'Name, date of birth, contact details, NHS and National Insurance numbers, support needs, consents, photo, who lives in each tenancy, and complaints and other communications with their responses',
        records: [
          record('Residents', [
            ['Who they are', [
              ['First name', 'Jordan'],
              ['Last name', 'Hale'],
              ['Date of birth', '14 Jul 1999'],
              ['Photo', 'A photo for their file'],
              ['Our reference', 'RES-0412'],
            ]],
            ['Contact', [
              ['Email address', 'jordan.hale@gmail.com'],
              ['Phone number', '07700 900789'],
            ]],
            ['Official numbers', [
              ['NHS number', '485 777 3456'],
              ['National Insurance number', 'QQ 12 34 56 C'],
            ]],
            ['Needs and consent', [
              ['Support needs', 'Mental health, managing money'],
              ['Consents they gave', 'Share with their GP, keep a photo on file'],
            ]],
          ]),
          record('Tenancy members', [
            ['Household', [
              ['Which tenancy they are on', 'Room 4, Beech House'],
            ]],
          ]),
          record('Communications', [
            ['What was raised', [
              ['Kind', 'Complaint'],
              ['How it came in', 'Phone'],
              ['When it was raised, and by whom', '6 Oct 2026, Kim Lowe'],
              ['What they said', '“The heating has been off for three days”'],
            ]],
            ['Our response', [
              ['The response', '“Engineer booked for tomorrow morning”'],
              ['When, and by whom', '6 Oct 2026, Kim Lowe'],
              ['Status', 'Resolved'],
              ['How it was resolved', '“Boiler part replaced, resident happy”'],
            ]],
          ]),
        ],
      },
      {
        key: 'app-support',
        name: 'Support and risk',
        section: 'BedMaps app',
        summary: 'Support plans with goals, risk levels and signatures, key work sessions, support logs, assessments, the risk register by area of risk, and outside support contacts',
        records: [
          record('Support plans', [
            ['The plan', [
              ['Kind of plan', 'Move-on plan'],
              ['Goals', '“Open a bank account, register with a GP”'],
              ['Risk level', 'Medium'],
              ['Status', 'Active'],
              ['Notes', '“Prefers morning sessions”'],
            ]],
            ['Dates', [
              ['Start date', '1 Mar 2025'],
              ['Review date', '1 Dec 2026'],
              ['End date', 'None yet'],
            ]],
            ['Workers', [
              ['Support worker', 'Kim Lowe'],
              ['Deputy worker', 'Lee Grant'],
            ]],
            ['Signatures', [
              ['Signed by the worker, and when', 'Kim Lowe, 2 Mar 2025'],
              ['Signed by the resident, and when', '2 Mar 2025'],
            ]],
          ]),
          record('Key work sessions', [
            ['Each session', [
              ['Date', '21 Oct 2026'],
              ['Support worker', 'Kim Lowe'],
              ['Session notes', '“Talked through a weekly budget”'],
              ['Actions', '“Book a GP appointment”'],
            ]],
          ]),
          record('Support logs', [
            ['Each log', [
              ['Kind of log', 'Phone call'],
              ['Date', '22 Oct 2026'],
              ['How long it took', '30 minutes'],
              ['Summary', '“Called about a missed rent payment”'],
              ['Actions taken', 'Set up a payment plan'],
              ['Attachments', 'Payment plan letter'],
              ['Follow-up needed, and when', 'Yes, 29 Oct 2026'],
              ['Support worker', 'Kim Lowe'],
              ['Which support plan', 'Move-on plan'],
            ]],
          ]),
          record('Assessments', [
            ['Each assessment', [
              ['Situation', '“Missed two rent payments”'],
              ['What triggered it', 'Arrears'],
              ['The assessment', '“Struggling with money since losing work”'],
              ['Action', 'Referral to debt advice'],
              ['Risk level', 'High'],
              ['Who assessed, and when', 'Kim Lowe, 15 Oct 2026'],
              ['Linked incident or plan', 'Move-on plan'],
              ['Review date and notes', '15 Nov 2026, “Paying £10 a week now”'],
              ['Status', 'Open'],
            ]],
          ]),
          record('Risk register', [
            ['Each risk', [
              ['Area of risk', 'Self-neglect'],
              ['Level', 'Medium'],
              ['Situation', '“Not eating regular meals”'],
              ['What triggers it', 'Low mood'],
              ['Action needed', 'Weekly cooking session'],
              ['Due and review dates', 'Due 1 Nov, review 1 Dec 2026'],
              ['Status', 'Active'],
              ['Notes', '“Agreed with Jordan”'],
            ]],
            ['Signatures', [
              ['Signed by the worker, and when', 'Kim Lowe, 20 Oct 2026'],
              ['Signed by the resident, and when', '20 Oct 2026'],
            ]],
          ]),
          record('Support contacts', [
            ['Each contact', [
              ['Kind of contact', 'GP surgery'],
              ['Organisation', 'Grove Lane Surgery'],
              ['Contact name', 'Reception'],
              ['Phone number', '0113 496 0456'],
              ['Email address', 'grovelane.surgery@nhs.net'],
              ['Address', '2 Grove Lane, Leeds'],
              ['When they are open', 'Mon to Fri, 8am to 6pm'],
              ['Notes', '“Ask for the duty doctor”'],
            ]],
          ]),
        ],
      },
      {
        key: 'app-safeguarding',
        name: 'Incidents and safeguarding',
        section: 'BedMaps app',
        summary: 'Incidents (what happened, when, how serious, who was involved, referrals made) and safeguarding alerts with their multi-agency reference and how they were resolved',
        records: [
          record('Incidents', [
            ['What happened', [
              ['Kind of incident', 'Antisocial behaviour'],
              ['Description', '“Shouting in the hallway late at night”'],
              ['When it happened', '18 Oct 2026, 23:40'],
              ['How serious', 'High'],
            ]],
            ['Who and where', [
              ['Property', 'Beech House'],
              ['Resident involved', 'Jordan Hale'],
              ['Who reported it', 'Kim Lowe'],
            ]],
            ['Follow-up', [
              ['Referrals made', 'Police, safeguarding team'],
              ['Attachments', 'Photo of the damaged door'],
              ['Status', 'Under review'],
            ]],
          ]),
          record('Safeguarding alerts', [
            ['The alert', [
              ['Kind of alert', 'Financial abuse'],
              ['How serious', 'High'],
              ['What happened', '“A visitor is taking Jordan’s bank card”'],
              ['When it was raised, and by whom', '19 Oct 2026, Kim Lowe'],
              ['Multi-agency reference', 'MASH-2026-3381'],
            ]],
            ['Outcome', [
              ['Status', 'Resolved'],
              ['When it was resolved, and by whom', '24 Oct 2026, Kim Lowe'],
              ['How it was resolved', '“Card cancelled, visitor banned”'],
            ]],
          ]),
        ],
      },
      {
        key: 'app-referrals',
        name: 'Referrals',
        section: 'BedMaps app',
        summary: 'People referred in: name, date of birth, gender, current address, contact details, who referred them and why, safeguarding concerns, assessment bookings and results, missed appointments, contact attempts, the decision, move-in date and room, and every step along the way',
        records: [
          record('The person', [
            ['Who', [
              ['First name', 'Jordan'],
              ['Last name', 'Hale'],
              ['Date of birth', '14 Jul 1999'],
              ['Gender', 'Non-binary'],
            ]],
            ['Contact', [
              ['Current address', 'Leeds night shelter'],
              ['Phone number', '07700 900789'],
              ['Email address', 'jordan.hale@gmail.com'],
            ]],
          ]),
          record('The referral', [
            ['Details', [
              ['Reference', 'REF-2026-0219'],
              ['Date', '10 Feb 2025'],
              ['Urgency', 'High'],
              ['Reason', '“Rough sleeping for two weeks”'],
              ['Initial assessment', '“Low support needs, ready for shared living”'],
            ]],
            ['Who referred them', [
              ['Where it came from', 'Council housing options'],
              ['More about where it came from', 'Leeds single homeless team'],
              ['Council', 'Leeds'],
              ['Referring agency contact', 'Hannah Price'],
            ]],
            ['Safeguarding', [
              ['Whether there is a concern', 'Yes'],
              ['Safeguarding notes', '“Known to the mental health team”'],
            ]],
            ['Handling', [
              ['Assessment officer', 'Kim Lowe'],
              ['Status', 'Assessment booked'],
            ]],
          ]),
          record('Assessment', [
            ['Booking', [
              ['Date and time', '14 Feb 2025, 11am'],
              ['Where', 'Beech House office'],
              ['Rebooked, and why', '“Jordan was in hospital”'],
            ]],
            ['Missed appointments', [
              ['How many were missed', '1'],
              ['Dates missed', '12 Feb 2025'],
            ]],
            ['Result', [
              ['When it was done', '14 Feb 2025'],
              ['Outcome', 'Suitable'],
              ['Notes', '“Would suit a ground-floor room”'],
            ]],
          ]),
          record('Contact attempts', [
            ['Each attempt', [
              ['Date and time', '11 Feb 2025, 2pm'],
              ['How', 'Phone'],
              ['Notes', '“No answer, left a voicemail”'],
            ]],
          ]),
          record('Outcome', [
            ['Decision', [
              ['Outcome', 'Accepted'],
              ['Why it was declined', 'Needs higher support'],
              ['More detail', '“Referred on to a 24-hour service”'],
            ]],
            ['Moving in', [
              ['Move-in date', '1 Mar 2025'],
              ['Property and room', 'Beech House, Room 4'],
              ['Move-in notes', '“Keys collected at 10am”'],
              ['Days from referral to move-in', '19'],
              ['Linked resident record', 'Jordan Hale'],
            ]],
          ]),
          record('Timeline', [
            ['Each step', [
              ['Date', '14 Feb 2025'],
              ['What happened', 'Assessment done'],
              ['Status before and after', 'Booked, then assessed'],
              ['Notes', '“Offer to follow”'],
            ]],
          ]),
        ],
      },
      {
        key: 'app-tenancies',
        name: 'Tenancies and rent',
        section: 'BedMaps app',
        summary: 'Tenancies (dates, rent, deposit, tenure, benefits), rent payments, arrears and action plans, and the full rent and service charge breakdown for each property, down to furniture bought',
        records: [
          record('Tenancies', [
            ['The tenancy', [
              ['Which room', 'Room 4, Beech House'],
              ['Tenure type', 'Licence'],
              ['Status', 'Active'],
            ]],
            ['Dates', [
              ['Start date', '1 Mar 2025'],
              ['End date', 'None yet'],
            ]],
            ['Rent', [
              ['Rent amount', '£230'],
              ['How often', 'Weekly'],
              ['Paid by Housing Benefit or Universal Credit', 'Housing Benefit'],
            ]],
            ['Deposit', [
              ['Deposit scheme', 'Deposit Protection Service'],
              ['Deposit reference', 'DPS-889211'],
            ]],
          ]),
          record('Rent payments', [
            ['Each payment', [
              ['Amount', '£230'],
              ['Date paid', '3 Oct 2026'],
              ['How it was paid', 'Housing Benefit'],
              ['Reference', 'HB-55120'],
              ['Notes', '“Paid direct by the council”'],
            ]],
          ]),
          record('Arrears', [
            ['Each case', [
              ['Which tenancy', 'Room 4, Beech House'],
              ['Balance owed', '£460'],
              ['When it was opened', '1 Sep 2026'],
              ['Status', 'Open'],
              ['Action plan', '“£10 a week taken from Universal Credit”'],
            ]],
          ]),
          record('Rent setting', [
            ['Rent', [
              ['Weekly rent', '£230'],
              ['Total weekly gross rent', '£310'],
              ['Local Housing Allowance rate', '£98.50'],
              ['Yearly increase', '3%'],
              ['Rent start date', '1 Mar 2025'],
              ['First rent amount and date', '£230 on 3 Mar 2025'],
              ['Rent-free weeks', '0'],
              ['When it takes effect, and status', '1 Apr 2026, active'],
            ]],
            ['Deposit', [
              ['Deposit amount', '£230'],
              ['Date paid', '1 Mar 2025'],
            ]],
            ['Shared costs, per week', [
              ['Communal cleaning', '£8.50'],
              ['Communal fuel', '£12'],
              ['Communal water', '£4'],
              ['Communal Wi-Fi', '£3'],
              ['Gardening', '£2'],
              ['Window cleaning', '£1'],
              ['Laundry', '£2.50'],
              ['CCTV', '£1.50'],
              ['White goods', '£2'],
              ['Council tax', '£6'],
              ['Other environmental costs', '£0.50'],
              ['Units, weeks and weekly rate', '6 rooms, 52 weeks, £45'],
              ['Total weekly shared charges', '£45'],
            ]],
            ['Personal costs, per week', [
              ['Utilities', '£10'],
              ['Cleaning', '£0'],
              ['Satellite TV', '£0'],
              ['Telephone', '£0'],
              ['Support', '£0'],
              ['Units, weeks and weekly rate', '6 rooms, 52 weeks, £10'],
              ['Total charges benefits will not cover', '£10'],
            ]],
            ['Housing management costs', [
              ['Housing staff and salaries', '£18 a week'],
              ['Pensions', '£1.20 a week'],
              ['IT', '£0.80 a week'],
              ['Signage', '£0.10 a week'],
              ['Units and business units', '6 rooms, 1 unit'],
              ['Weekly rate and weeks', '£22 over 52 weeks'],
            ]],
            ['Office costs', [
              ['Office rent', '£2.40 a week'],
              ['Salaries and staff', '£6 a week'],
              ['Pensions', '£0.50 a week'],
              ['Audit, bank and professional fees', '£1.10 a week'],
              ['Consultancy', '£0.40 a week'],
              ['IT, hardware and software', '£0.90 a week'],
              ['Printing, telephone and subscriptions', '£0.60 a week'],
              ['Recruitment, training and travel', '£0.70 a week'],
              ['Signage', '£0.05 a week'],
              ['Units, weeks and weekly rate', '6 rooms, 52 weeks, £12.65'],
            ]],
            ['Safety tests, cost and how often', [
              ['Gas safety', '£90, every 12 months'],
              ['Electrical certificate', '£180, every 5 years'],
              ['Fire alarm', '£120, every 6 months'],
              ['Emergency lighting', '£60, every 12 months'],
              ['Fire risk assessment', '£250, every 12 months'],
              ['Asbestos', '£300, once'],
              ['Energy certificate', '£80, every 10 years'],
              ['Appliance testing', '£75, every 12 months'],
              ['Insurance', '£900, every 12 months'],
              ['Tenants, weeks and weekly rate', '6 tenants, 52 weeks, £5.20'],
            ]],
            ['Other costs', [
              ['Minor repairs', '£4 a week'],
              ['Redecoration', '£2 a week'],
              ['Sinking fund', '£3 a week'],
              ['Voids', '£2.50 a week'],
              ['Housing provider management fee', '£5 a week'],
              ['Agent fee', '10%'],
              ['Support: housing officer cost, tenants, weeks and weekly rate', '£300 a week, 6 tenants, 52 weeks, £50'],
            ]],
          ]),
          record('Furniture and items', [
            ['Each item', [
              ['Shared or in a room', 'Shared'],
              ['Item', 'Washing machine'],
              ['Supplier', 'Currys'],
              ['Quantity', '1'],
              ['Cost', '£320'],
              ['Whether it was bought again', 'No'],
            ]],
          ]),
        ],
      },
      {
        key: 'app-properties',
        name: 'Properties and schemes',
        section: 'BedMaps app',
        summary: 'Schemes and who they are for, each property (address, type, HMO, ownership, meters, suppliers, key safe and Wi-Fi details, landlord and agent), communal areas, rooms with sizes and door codes, contacts, leases with housing associations, empty periods and lost rent, and equipment',
        records: [
          record('Schemes', [
            ['The scheme', [
              ['Name', 'Beech House Young People’s Service'],
              ['Reference', 'SCH-07'],
              ['Description', '“Shared houses for young people leaving care”'],
              ['Statement of purpose', '“Helping young people build skills to live on their own”'],
              ['Kind of service', 'Supported housing'],
              ['Status', 'Active'],
            ]],
            ['Who it is for', [
              ['Age range', '18 to 25'],
              ['Gender rule', 'Mixed'],
              ['Who can live there', '“Care leavers with a local connection to Leeds”'],
              ['Who cannot', '“High-risk offending history”'],
            ]],
            ['Running it', [
              ['Funding', 'Housing Benefit and a council contract'],
              ['Commissioned by', 'Leeds City Council'],
              ['Staffing', 'Staff on site 9am to 5pm'],
              ['Total rooms', '18'],
            ]],
          ]),
          record('Properties', [
            ['The property', [
              ['Name', 'Beech House'],
              ['Scheme', 'Beech House Young People’s Service'],
              ['Property type', 'House'],
              ['HMO', 'Yes'],
              ['Ownership', 'Leased'],
              ['Status', 'Active'],
              ['Photo', 'A photo of the front of the house'],
              ['Property reference number', 'UPRN 72745632'],
            ]],
            ['Where', [
              ['Address', '12 Grove Lane, Leeds, West Yorkshire LS6 4AA'],
              ['Map position', 'Near Headingley, Leeds'],
              ['Council', 'Leeds'],
              ['Council tax band', 'B'],
            ]],
            ['Size', [
              ['Bedrooms', '6'],
              ['Floors', '3'],
              ['Communal areas', 'Kitchen, lounge, garden'],
            ]],
            ['Suppliers', [
              ['Gas and electricity, shared and for rooms', 'British Gas for shared areas, prepay meters in rooms'],
              ['Water', 'Yorkshire Water'],
              ['Broadband', 'BT'],
            ]],
            ['Where things are', [
              ['Fuse box', 'Under the stairs'],
              ['Gas, electricity and water meters', 'Cupboard by the front door'],
              ['Stopcock', 'Under the kitchen sink'],
              ['Boiler', 'Kitchen cupboard'],
            ]],
            ['Access and log-ins', [
              ['Key safe location and code', 'By the back door, 4417'],
              ['Wi-Fi name and password', 'BeechHouse, sunflower22'],
              ['Meter type, account log-in and top-up codes', 'Prepay, account username and password'],
            ]],
            ['People', [
              ['Landlord', 'Ravi Shah'],
              ['Managing agent', 'Parkside Lettings'],
              ['Housing provider', 'Northgate Homes'],
            ]],
          ]),
          record('Communal areas', [
            ['Each area', [
              ['Name', 'Shared kitchen'],
              ['Kind of area', 'Kitchen'],
              ['Floor', 'Ground'],
              ['Size', '“Large, seats six”'],
              ['Notes', '“Fire door must stay shut”'],
            ]],
          ]),
          record('Rooms', [
            ['The room', [
              ['Reference', 'Room 4'],
              ['Kind of room', 'Single'],
              ['Status', 'Let'],
              ['Floor', 'First'],
            ]],
            ['Size', [
              ['Area', '11 square metres'],
              ['Length and width', '3.6 by 3.1 metres'],
              ['Bedrooms and bathrooms', '1 bedroom, shared bathroom'],
            ]],
            ['Facilities', [
              ['En-suite', 'No'],
              ['Furnished', 'Furnished'],
              ['Accessible, and access features', 'No'],
            ]],
            ['Money', [
              ['Rent a month', '£996'],
              ['Service charge a month', '£195'],
            ]],
            ['Door code', [
              ['Door code', '2580'],
              ['When it was changed, and by whom', '2 Oct 2026, Kim Lowe'],
            ]],
          ]),
          record('Contacts', [
            ['Each contact', [
              ['Kind of contact', 'Landlord'],
              ['Name', 'Ravi Shah'],
              ['Organisation', 'Shah Properties'],
              ['Email address', 'ravi@shahproperties.co.uk'],
              ['Phone and mobile', '0113 496 0789, 07700 900321'],
              ['Address', '8 Kirkgate, Leeds'],
              ['Photo', 'A profile photo'],
              ['Status and notes', 'Active, “Prefers email”'],
            ]],
            ['At each property', [
              ['Which property', 'Beech House'],
              ['Their role there', 'Landlord'],
              ['Main contact or not', 'Main contact'],
            ]],
          ]),
          record('Housing association lease', [
            ['The lease', [
              ['Housing association', 'Northgate Homes'],
              ['Contact', 'Amy Ford'],
              ['Date signed', '1 Feb 2025'],
              ['Start and end', '1 Mar 2025 to 28 Feb 2030'],
              ['Term', '60 months'],
              ['Notice period', '90 days'],
              ['Furnished', 'Yes'],
              ['Notes', '“Break clause at year three”'],
            ]],
            ['Documents', [
              ['Lease', 'lease-beech-house.pdf'],
              ['Service agreement', 'sla-northgate.pdf'],
              ['Inventory', 'inventory-march-2025.pdf'],
            ]],
          ]),
          record('Empty periods', [
            ['Each period', [
              ['Which room', 'Room 2'],
              ['Start and end', '1 Aug to 21 Aug 2026'],
              ['Reason and detail', 'Moved on, “Room repainted before relet”'],
              ['Expected rent lost', '£690'],
              ['Expected service charge lost', '£135'],
              ['Total expected loss', '£825'],
              ['Tenancies before and after', 'Ended: Sam Cole; started: Jordan Hale'],
              ['Still empty or not', 'No'],
            ]],
          ]),
          record('Equipment', [
            ['Each item', [
              ['Kind', 'Boiler'],
              ['Make and model', 'Worcester Greenstar 30i'],
              ['Serial number', 'WB-4471209'],
              ['Where it is', 'Kitchen cupboard'],
              ['Installed', 'Jan 2021'],
              ['Warranty until', 'Jan 2028'],
              ['Status', 'Working'],
            ]],
          ]),
        ],
      },
      {
        key: 'app-repairs',
        name: 'Repairs and maintenance',
        section: 'BedMaps app',
        summary: 'Repair requests (what, where, priority, photos, who reported them, hazard and the time limits for each stage), work orders, contractors with company, director, Gas Safe and DBS details, quotes and costs, planned maintenance and its history, and the public repair and contractor forms',
        records: [
          record('Repair requests', [
            ['The problem', [
              ['Reference', 'REP-1093'],
              ['Category and subcategory', 'Plumbing, leaking tap'],
              ['Description', '“Kitchen tap drips all the time”'],
              ['Where', 'Room 4, kitchen, under the sink'],
              ['Photos', 'Two photos'],
              ['Priority', 'Urgent'],
            ]],
            ['Who reported it', [
              ['Name', 'Jordan Hale'],
              ['Email address', 'jordan.hale@gmail.com'],
              ['Phone number', '07700 900789'],
              ['How it came in', 'Repair form'],
              ['The email it came from', 'An email to the repairs inbox'],
            ]],
            ['Hazard and time limits', [
              ['Hazard', 'Damp and mould'],
              ['Which standard it falls under', 'Damp and mould standard, version 2'],
              ['When we became aware', '20 Oct 2026, 09:10'],
              ['When it was made safe', '20 Oct 2026, 15:00'],
              ['When it was investigated', '21 Oct 2026'],
              ['When the summary was sent', '22 Oct 2026'],
              ['When works started and finished', '24 Oct to 25 Oct 2026'],
              ['Target time', '24 hours'],
            ]],
            ['Handling', [
              ['Status', 'Works booked'],
              ['Triage outcome', 'Send a contractor'],
              ['Contractor given it', 'Brightline Plumbing Ltd'],
              ['What the investigation found', '“Worn washer, no water damage”'],
              ['Next steps', '“Replace the washer”'],
              ['Invoice', 'invoice-1093.pdf'],
              ['Notes', '“Resident home after 4pm”'],
              ['Why it was deleted', 'Logged twice'],
            ]],
          ]),
          record('Repair standards', [
            ['Each standard', [
              ['Name', 'Damp and mould'],
              ['Regulator', 'Regulator of Social Housing'],
              ['Description', '“Time limits for hazards in social homes”'],
              ['Versions and the dates they apply', 'Version 2, from 27 Oct 2025'],
              ['Working days calendar', 'Mon to Fri, without bank holidays'],
            ]],
            ['Hazards and time limits', [
              ['Each hazard and how serious it is', 'Damp and mould, emergency'],
              ['Each stage and its time limit', 'Make safe within 24 hours'],
              ['When it counts as at risk', 'At 75% of the time used'],
            ]],
          ]),
          record('Work orders', [
            ['The order', [
              ['Reference', 'WO-2207'],
              ['Title', 'Replace kitchen tap washer'],
              ['Description', '“Room 4 kitchen, tap drips”'],
              ['Priority', 'Urgent'],
              ['Linked repair', 'REP-1093'],
              ['Status', 'Done'],
            ]],
            ['Timing', [
              ['Scheduled date', '24 Oct 2026'],
              ['Completion date', '25 Oct 2026'],
            ]],
            ['Money', [
              ['Estimated cost', '£90'],
              ['Actual cost', '£85'],
            ]],
            ['Contractor', [
              ['Contractor', 'Brightline Plumbing Ltd'],
              ['Their notes', '“Washer replaced, tested”'],
              ['Completion photos', 'Photo of the fixed tap'],
            ]],
          ]),
          record('Contractors', [
            ['The company', [
              ['Company name', 'Brightline Plumbing Ltd'],
              ['Company number', '12345678'],
              ['Registered address', '5 Mill Lane, Leeds LS11 5AA'],
              ['Date it was set up', '3 Jun 2016'],
              ['Directors', 'Dan Hughes'],
              ['Logo', 'Company logo'],
            ]],
            ['Contact', [
              ['Contact name', 'Dan Hughes'],
              ['Phone number', '0113 496 0999'],
              ['Email address', 'jobs@brightlineplumbing.co.uk'],
              ['Address', '5 Mill Lane, Leeds'],
            ]],
            ['Checks', [
              ['Services', 'Plumbing, heating'],
              ['Industry accreditations', 'Gas Safe, CHAS'],
              ['Trade certificates', 'Gas safety, unvented hot water'],
              ['Gas Safe number', '654321'],
              ['DBS certificate number', '001234567890'],
              ['Whether staff carry photo ID', 'Yes'],
              ['Insurance expiry', '31 Mar 2027'],
            ]],
            ['Our view', [
              ['Rating', '4.5 out of 5'],
              ['Status', 'Approved'],
              ['Notes', '“Quick on emergencies”'],
            ]],
          ]),
          record('Contractor jobs', [
            ['Each job', [
              ['Contractor', 'Brightline Plumbing Ltd'],
              ['Work order', 'WO-2207'],
              ['Quoted amount', '£90'],
              ['Actual amount', '£85'],
              ['Start and finish dates', '24 Oct to 25 Oct 2026'],
              ['Status', 'Paid'],
              ['Notes', '“Invoice received”'],
            ]],
          ]),
          record('Planned maintenance', [
            ['The schedule', [
              ['Reference', 'PM-031'],
              ['Title', 'Boiler service'],
              ['Kind', 'Servicing'],
              ['Description', '“Yearly boiler service”'],
              ['Which property, room or equipment', 'Beech House boiler'],
              ['Priority', 'Normal'],
              ['Status', 'Active'],
              ['Notes', '“Book with the gas check”'],
            ]],
            ['Timing', [
              ['How often', 'Every 12 months'],
              ['Next due', '14 Jan 2027'],
              ['Last done', '14 Jan 2026'],
            ]],
            ['Who and cost', [
              ['Assigned to', 'Kim Lowe'],
              ['Contractor', 'Brightline Plumbing Ltd'],
              ['Estimated cost', '£90'],
            ]],
          ]),
          record('Maintenance history', [
            ['Each visit', [
              ['Date done', '14 Jan 2026'],
              ['Done by', 'Brightline Plumbing Ltd'],
              ['Work order', 'WO-1802'],
              ['Actual cost', '£85'],
              ['Notes', '“All fine”'],
            ]],
          ]),
          record('Public forms', [
            ['Repair form links', [
              ['Who it was sent to: name, email and phone', 'Jordan Hale, jordan.hale@gmail.com, 07700 900789'],
              ['Which property, room or communal area', 'Beech House, Room 4'],
              ['Where the link was shared', 'Text message'],
              ['IP address when it was used', '81.2.69.160'],
              ['When it was used, and when it expires', 'Used 20 Oct 2026, expires 27 Oct 2026'],
            ]],
            ['Contractor update links', [
              ['Contractor name, email and phone', 'Dan Hughes, jobs@brightlineplumbing.co.uk, 0113 496 0999'],
              ['Which work order', 'WO-2207'],
              ['IP address when it was used', '81.2.69.161'],
              ['When it was used, and when it expires', 'Used 25 Oct 2026, expires 1 Nov 2026'],
            ]],
          ]),
        ],
      },
      {
        key: 'app-safety',
        name: 'Safety checks and inspections',
        section: 'BedMaps app',
        summary: 'Safety certificates, safety tests with the tester’s details, inspections of properties, rooms and communal areas with each item checked, findings turned into repairs, and the public inspection and safety test forms',
        records: [
          record('Certificates', [
            ['Each certificate', [
              ['Kind', 'Gas safety'],
              ['Which property, room or equipment', 'Beech House boiler'],
              ['Issue date', '14 Jan 2026'],
              ['Expiry date', '14 Jan 2027'],
              ['Outcome', 'Pass'],
              ['The file', 'gas-safety-2026.pdf'],
              ['Notes', '“No issues”'],
            ]],
          ]),
          record('Safety tests', [
            ['The test', [
              ['Kind of test', 'Fire alarm'],
              ['Which property or room', 'Beech House'],
              ['Scheduled and completed dates', '1 Oct and 2 Oct 2026'],
              ['Status', 'Done'],
            ]],
            ['The tester', [
              ['Tester', 'Kim Lowe'],
              ['Who carried it out', 'Firecheck Ltd'],
              ['Tester name, phone and email', 'Mark Ellis, 0113 496 0222, mark@firecheck.co.uk'],
            ]],
            ['Result', [
              ['Outcome', 'Pass'],
              ['Overall condition', 'Good'],
              ['Findings', '“One detector slow to sound”'],
              ['Photos', 'Photo of the panel'],
              ['Actions needed', 'Replace the landing detector'],
              ['Notes', '“Next test in six months”'],
            ]],
          ]),
          record('Inspections', [
            ['The inspection', [
              ['Kind', 'Room check'],
              ['Which property, room or communal area', 'Beech House, Room 4'],
              ['Scheduled and completed dates', '10 Oct and 10 Oct 2026'],
              ['Status', 'Done'],
              ['Inspector and who carried it out', 'Kim Lowe'],
            ]],
            ['Result', [
              ['Overall condition', 'Good'],
              ['Outcome', 'Pass with actions'],
              ['Findings', '“Small patch of mould by the window”'],
              ['Photos', 'Three photos'],
              ['Actions needed', 'Treat the mould'],
              ['Notes', '“Resident keeps the room tidy”'],
            ]],
            ['Follow-up', [
              ['Whether it was reported to maintenance', 'Yes'],
              ['Verified, when and by whom', '12 Oct 2026, Lee Grant'],
            ]],
          ]),
          record('Inspection items', [
            ['Each item checked', [
              ['Section', 'Kitchen'],
              ['Item', 'Smoke alarm'],
              ['Condition', 'Working'],
              ['Defects', 'None'],
              ['Actions', 'None'],
              ['Photos', 'Photo of the alarm'],
            ]],
          ]),
          record('Inspection findings', [
            ['Each finding', [
              ['Where', 'Room 4, by the window'],
              ['Description', '“Black mould on the window frame”'],
              ['How serious', 'Medium'],
              ['Photos', 'Close-up photo'],
            ]],
            ['What happened next', [
              ['Turned into a repair, when and by whom', 'REP-1101, 10 Oct 2026, Kim Lowe'],
              ['Dismissed, when, by whom and why', 'Not dismissed'],
            ]],
          ]),
          record('Public forms', [
            ['Inspection links', [
              ['Who it was sent to: name, email and phone', 'Mark Ellis, mark@firecheck.co.uk, 0113 496 0222'],
              ['Which property, room and kind of inspection', 'Beech House, room check'],
              ['IP address when it was used', '81.2.69.162'],
              ['When it was used, and when it expires', 'Used 10 Oct 2026, expires 17 Oct 2026'],
            ]],
            ['Safety test links', [
              ['Who it was sent to: name, email and phone', 'Mark Ellis, mark@firecheck.co.uk, 0113 496 0222'],
              ['Which property, room and kind of test', 'Beech House, fire alarm'],
              ['IP address when it was used', '81.2.69.163'],
              ['When it was used, and when it expires', 'Used 2 Oct 2026, expires 9 Oct 2026'],
            ]],
          ]),
        ],
      },
      {
        key: 'app-utilities',
        name: 'Utilities and council tax',
        section: 'BedMaps app',
        summary: 'Utility contracts, suppliers and bills, meter readings with photos and who sent them, the public meter reading form, and council tax accounts, direct debits and payments',
        records: [
          record('Utility contracts', [
            ['The contract', [
              ['Supplier', 'British Gas'],
              ['Kind', 'Electricity'],
              ['Account number', '850012345678'],
              ['Start and end', '1 Apr 2026 to 31 Mar 2028'],
              ['Term', '24 months'],
              ['Renews by itself', 'No'],
              ['Status', 'Active'],
              ['Notes', '“Fixed rate”'],
            ]],
            ['Meter', [
              ['Meter serial number', 'E10BG12345'],
              ['Electricity supply number', '1200 0123 4567 890'],
              ['Gas supply number', '1234567890'],
            ]],
            ['Costs', [
              ['Unit rate', '24.5p per unit'],
              ['Standing charge', '53p a day'],
              ['Estimated monthly cost', '£210'],
              ['How often they bill', 'Monthly'],
            ]],
          ]),
          record('Utility suppliers', [
            ['Each supplier', [
              ['Supplier and kind', 'Yorkshire Water, water'],
              ['Account number', 'YW-998812'],
              ['Start and end', 'From 1 Mar 2025'],
              ['Meter serial and supply numbers', 'W12345678'],
              ['Notes', '“Metered”'],
            ]],
          ]),
          record('Utility bills', [
            ['Each bill', [
              ['Which contract and property', 'British Gas electricity, Beech House'],
              ['Bill date', '5 Oct 2026'],
              ['Period covered', '1 Sep to 30 Sep 2026'],
              ['Amount', '£198.40'],
              ['Status', 'Paid'],
              ['When and how it was paid, and the reference', '12 Oct 2026, direct debit, BG-77120'],
              ['Notes', '“Estimated reading”'],
            ]],
          ]),
          record('Meter readings', [
            ['Each reading', [
              ['Meter type', 'Electricity'],
              ['Reading', '45210'],
              ['Date', '1 Oct 2026'],
              ['Photo of the meter', 'meter-oct.jpg'],
              ['Meter and supply numbers', 'E10BG12345, 1200 0123 4567 890'],
              ['Which property or room, and inspection', 'Beech House, room check'],
              ['Recorded by', 'Kim Lowe'],
              ['Sent in by, and their phone', 'Jordan Hale, 07700 900789'],
              ['Notes', '“Read at 9am”'],
            ]],
          ]),
          record('Public meter form', [
            ['Meter reading links', [
              ['Who it was sent to: name and phone', 'Jordan Hale, 07700 900789'],
              ['Which property, room and meters', 'Beech House, Room 4, electricity'],
              ['Where the link was shared', 'Text message'],
              ['IP address when it was used', '81.2.69.164'],
              ['When it was used, and when it expires', 'Used 1 Oct 2026, expires 8 Oct 2026'],
            ]],
          ]),
          record('Council tax', [
            ['The account', [
              ['Account number', '30012345'],
              ['Tax year', '2026 to 2027'],
              ['Band', 'B'],
              ['Yearly cost', '£1,720'],
              ['Monthly payment and how often', '£172, monthly'],
              ['Balance', '£860'],
              ['Liable from', '1 Mar 2025'],
              ['The bill', 'council-tax-2026.pdf'],
              ['Notes', '“Ten payments a year”'],
            ]],
            ['Direct debit', [
              ['Reference', 'DD-55102'],
              ['Status', 'Active'],
              ['Set-up date and first payment', '20 Mar 2026, 1 Apr 2026'],
            ]],
            ['Accountant', [
              ['Whether to send to the accountant, and when it was sent', 'Yes, 2 Apr 2026'],
            ]],
          ]),
          record('Council tax payments', [
            ['Each payment', [
              ['Amount', '£172'],
              ['Date', '1 Oct 2026'],
              ['How it was paid', 'Direct debit'],
              ['Reference', 'DD-55102'],
              ['Notes', '“October payment”'],
            ]],
          ]),
        ],
      },
      {
        key: 'app-payments',
        name: 'Landlord payments and service charges',
        section: 'BedMaps app',
        summary: 'Landlords’ bank details (last four digits only), payment schedules with agent fees and deductions, each payment and payment run, service charge income targets by benefit, and service charge receipts',
        records: [
          record('Landlord bank details', [
            ['Each account', [
              ['Landlord', 'Ravi Shah'],
              ['Bank name', 'Barclays'],
              ['Account name', 'R Shah'],
              ['Last four digits', '4821'],
              ['Account reference', 'BEECH-RENT'],
              ['Payment method', 'Bank transfer'],
              ['Main account or not', 'Main'],
              ['Notes', '“Pay by the 1st”'],
            ]],
          ]),
          record('Payment schedules', [
            ['Each schedule', [
              ['Property, landlord and managing agent', 'Beech House, Ravi Shah, Parkside Lettings'],
              ['Base rent', '£1,800 a month'],
              ['Agent fee', '10%, £180'],
              ['Service charge deduction', '5%'],
              ['Net payment', '£1,530'],
              ['Payment day and how often', 'The 1st, monthly'],
              ['Start and end', 'From 1 Mar 2025'],
              ['Status and notes', 'Active, “Review in March”'],
            ]],
          ]),
          record('Landlord payments', [
            ['Each payment', [
              ['Landlord and property', 'Ravi Shah, Beech House'],
              ['Period', '1 Oct to 31 Oct 2026'],
              ['Gross amount', '£1,800'],
              ['Agent fee', '£180'],
              ['Net amount', '£1,530'],
              ['Payment date', '1 Oct 2026'],
              ['References', 'PAY-1026, BEECH-RENT'],
              ['Status, and when it was processed', 'Paid, 1 Oct 2026'],
              ['Why it failed', 'Not failed'],
              ['Notes', '“On time”'],
            ]],
          ]),
          record('Payment runs', [
            ['Each run', [
              ['Run and batch reference', 'RUN-2026-10, BATCH-44'],
              ['Date', '1 Oct 2026'],
              ['Number of payments', '8'],
              ['Total', '£11,240'],
              ['Approved, by whom and when', 'Finance manager, 30 Sep 2026'],
              ['Processed', '1 Oct 2026'],
              ['Status and notes', 'Done, “All paid”'],
            ]],
          ]),
          record('Service charge targets', [
            ['Each target', [
              ['Property and financial year', 'Beech House, 2026 to 2027'],
              ['Council', 'Leeds'],
              ['Expected weekly, monthly and yearly', '£270, £1,170, £14,040'],
              ['From Housing Benefit', '£9,000'],
              ['From Universal Credit', '£4,000'],
              ['Paid privately', '£1,040'],
              ['Active, and notes', 'Active, “Based on six rooms”'],
            ]],
          ]),
          record('Service charge receipts', [
            ['Each receipt', [
              ['Property and room', 'Beech House, Room 4'],
              ['Amount', '£780'],
              ['Date', '6 Oct 2026'],
              ['Period covered', '1 Sep to 28 Sep 2026'],
              ['Paid by', 'Housing Benefit'],
              ['Council', 'Leeds'],
              ['References', 'HB-55120, claim 7781203'],
              ['Matched up, when and by whom', 'Yes, 7 Oct 2026, finance manager'],
              ['Notes', '“Four weeks”'],
            ]],
          ]),
        ],
      },
      {
        key: 'app-documents',
        name: 'Documents and notes',
        section: 'BedMaps app',
        summary: 'Documents and photos linked to properties, rooms, residents, tenancies, contractors, inspections, certificates and bills, confidential or not, with expiry dates; and notes on properties, rooms, residents and anything else',
        records: [
          record('Documents', [
            ['The document', [
              ['Title', 'Tenancy agreement, Room 4'],
              ['Kind', 'Tenancy agreement'],
              ['Description', '“Signed licence agreement”'],
              ['Tags', 'Tenancy, signed'],
              ['The file', 'licence-room-4.pdf'],
              ['File type and size', 'PDF, 310 KB'],
              ['Photo caption', '“Signed copy”'],
            ]],
            ['What it belongs to', [
              ['Property, room, resident or tenancy', 'Jordan Hale, Room 4'],
              ['Contractor, inspection, certificate or equipment', 'None'],
              ['Planned check, safety test, utility contract or bill', 'None'],
            ]],
            ['Handling', [
              ['Confidential or not', 'Confidential'],
              ['Expiry date', 'None'],
              ['Who uploaded it', 'Kim Lowe'],
            ]],
          ]),
          record('Notes', [
            ['Each note', [
              ['Title', 'Settling in'],
              ['The note', '“Jordan has settled well and likes the garden”'],
              ['Kind of note', 'General'],
              ['Important or not', 'Important'],
              ['Property, room or resident', 'Jordan Hale'],
            ]],
          ]),
          record('Activity notes', [
            ['Each note', [
              ['What it is about', 'A repair'],
              ['Kind of note', 'Call'],
              ['The note', '“Called the plumber, coming Friday”'],
            ]],
          ]),
        ],
      },
      {
        key: 'app-messages',
        name: 'Messages and email',
        section: 'BedMaps app',
        summary: 'Team chats and the shared inbox (who wrote in, the subject, who it is assigned to, response times), every message with attachments, mentions and reactions, every email, text and WhatsApp sent with whether it was opened or bounced, the templates behind them, sending addresses, and in-app notifications',
        records: [
          record('Conversations', [
            ['The conversation', [
              ['Kind', 'Shared inbox email'],
              ['Name, topic and subject', '“Repair at Beech House”'],
              ['Private or not', 'Private'],
              ['Who wrote in: name and email', 'Ravi Shah, ravi@shahproperties.co.uk'],
              ['What it is linked to', 'Repair REP-1093'],
              ['Starred, muted or archived', 'Starred'],
            ]],
            ['Handling', [
              ['Status', 'Resolved'],
              ['Assigned to, by whom and when', 'Kim Lowe, by Lee Grant, 20 Oct 2026'],
              ['First message in and first reply', '20 Oct 2026, 09:02 and 09:40'],
              ['When it was resolved', '25 Oct 2026'],
              ['Last message', '25 Oct 2026, 16:10'],
            ]],
            ['People in it', [
              ['Who is in it, and their role', 'Kim Lowe, owner'],
              ['When each person last read it', '25 Oct 2026, 16:12'],
              ['Muted, pinned or favourited', 'Pinned'],
            ]],
          ]),
          record('Messages', [
            ['The message', [
              ['Text', '“Plumber is booked for Friday”'],
              ['Subject', '“Re: Repair at Beech House”'],
              ['Who sent it', 'Kim Lowe'],
              ['In or out', 'Out'],
              ['Internal note or not', 'No'],
              ['Reply to', 'Ravi’s first email'],
              ['Pinned, when and by whom', 'Not pinned'],
              ['When it was edited', 'Not edited'],
            ]],
            ['Email details', [
              ['Sender address for incoming email', 'ravi@shahproperties.co.uk'],
              ['Email message reference', 'A reference used to keep email threads together'],
              ['A copy of the original email', 'Saved'],
            ]],
            ['Extras', [
              ['Attachments: file name, type and size', 'quote.pdf, PDF, 120 KB'],
              ['People mentioned', 'Lee Grant'],
              ['Reactions', 'Thumbs up from Lee Grant'],
            ]],
          ]),
          record('Emails, texts and WhatsApp sent', [
            ['Each message sent', [
              ['Email, text or WhatsApp', 'Text'],
              ['Who to', '07700 900789'],
              ['Subject and text, as sent', '“Your repair is booked for Friday”'],
              ['Which template and provider', 'Repair booked, Twilio'],
              ['What it is about', 'Repair REP-1093'],
              ['Which hazard stage it is for', 'Made safe'],
            ]],
            ['What happened to it', [
              ['Status', 'Delivered'],
              ['Sent, delivered and opened', '20 Oct 2026, 15:02 to 15:20'],
              ['Bounced', 'No'],
              ['Errors and attempts', 'None, 1 attempt'],
            ]],
            ['Changes', [
              ['Each change before it was sent, and by whom', 'Kim Lowe changed the wording'],
            ]],
          ]),
          record('Templates and sending', [
            ['Templates', [
              ['Name and description', 'Repair booked, “Tells the resident when the trade is coming”'],
              ['Email, text or WhatsApp', 'Text'],
              ['Language', 'English'],
              ['Subject and wording', '“Your repair is booked for {date}”'],
              ['When it sends by itself', 'When a repair is booked, straight away'],
            ]],
            ['Sending addresses', [
              ['Address and display name', 'repairs@oakwell.org.uk, Oakwell Repairs'],
              ['What each address is for', 'Repairs'],
              ['Council it belongs to', 'Leeds'],
            ]],
            ['Providers and safe list', [
              ['Email and text providers', 'Resend, Twilio'],
              ['Addresses allowed while testing', 'kim.lowe@oakwell.org.uk'],
            ]],
          ]),
          record('Notifications', [
            ['Each notification', [
              ['Title and message', '“New repair at Beech House”'],
              ['Kind and priority', 'Repair, urgent'],
              ['Who it is for', 'Kim Lowe'],
              ['What it links to', 'Repair REP-1093'],
              ['Read, and when', 'Read 20 Oct 2026'],
            ]],
            ['Daily figures', [
              ['Created, read, emailed, delivered and failed, per day', '14 created, 12 read, 6 emailed, 6 delivered, 0 failed'],
            ]],
          ]),
        ],
      },
      {
        key: 'app-staff',
        name: 'Staff and organisation',
        section: 'BedMaps app',
        summary: 'Staff profiles, job titles, roles and permissions, which tabs they see, their notification and display settings, the organisation’s details, quotas and usage, alert routing and scheduled reports, a log of every change and deletion, and the IP address and browser of anyone who hits the rate limits',
        records: [
          record('Staff', [
            ['Profile', [
              ['First and last name', 'Kim Lowe'],
              ['Display name and username', 'Kim, kimlowe'],
              ['Email address', 'kim.lowe@oakwell.org.uk'],
              ['Phone number', '07700 900555'],
              ['Photo', 'Profile photo'],
            ]],
            ['Role and access', [
              ['Job title', 'Support worker'],
              ['Role', 'Housing officer'],
              ['Permissions', 'View residents, edit repairs'],
              ['Which tabs they can see', 'Residents, repairs, messages'],
              ['Active or not, and their main account', 'Active, Oakwell'],
            ]],
            ['Settings', [
              ['Notifications: which kinds, how urgent, how often, quiet hours, sound', 'Repairs and urgent only, quiet 10pm to 7am'],
              ['Display: theme, time zone, date and time format, items per page', 'Dark, London, 24-hour, 25 a page'],
              ['Dashboard layout and design settings', 'Repairs first'],
            ]],
          ]),
          record('Organisation', [
            ['Details', [
              ['Name and type', 'Oakwell Supported Living, housing provider'],
              ['Support email and phone', 'help@oakwell.org.uk, 0113 496 0600'],
              ['Phone number', '0113 496 0600'],
              ['Registered address', '1 Park Square, Leeds LS1 2NE'],
              ['Time zone', 'London'],
            ]],
            ['Limits and usage', [
              ['Email, text and AI limits', '5,000 emails, 500 texts, £20 of AI a month'],
              ['Emails, texts and AI spend each month', '1,240 emails, 88 texts, £3.10 of AI'],
            ]],
            ['Settings', [
              ['Which modules are on', 'Repairs, support, finance'],
              ['Finance settings', 'Year starts in April, warn at 10% short'],
              ['Who gets each alert, and how', 'Urgent repairs to managers by text'],
              ['Scheduled reports: what, when and to whom', 'Weekly arrears report, Mondays at 8am, to finance'],
            ]],
          ]),
          record('Accounts and spaces', [
            ['The account', [
              ['Name and web name', 'Oakwell, oakwell'],
              ['Status', 'Active'],
            ]],
            ['Members', [
              ['Who belongs to the account', 'Kim Lowe'],
              ['Which account each sign-in is using', 'Oakwell'],
            ]],
          ]),
          record('History and security', [
            ['Change log', [
              ['Every change: which record, what changed, who and when', 'Kim Lowe changed Room 4’s rent on 1 Apr 2026'],
              ['A copy of the record at the time', 'Saved with each change'],
              ['Fields hidden from the log', 'Passwords and door codes'],
            ]],
            ['Deletions', [
              ['What was deleted, why and by whom', 'A duplicate repair, “Logged twice”, Kim Lowe'],
              ['Whether it can be restored, and when it was', 'Yes, not restored'],
            ]],
            ['Rate limits', [
              ['Who made too many requests: IP address and browser', '81.2.69.170, Chrome on Windows'],
              ['How many requests, and when', '120 in one minute, 20 Oct 2026'],
            ]],
            ['Search index', [
              ['Property details broken into pieces for AI search', '“Beech House: 6 rooms, boiler in the kitchen cupboard”'],
            ]],
          ]),
        ],
      },
    ],
  },
];
